import React, { useMemo, useRef, useCallback, useState, useEffect, useDeferredValue } from 'react';
import { flushSync } from 'react-dom';
import {
  ColumnDef,
  ColumnResizeMode,
  ColumnSizingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { buildSortableHeaderStyle } from './sortableHeaderStyle';
import { AlertCircle, BarChart3, RefreshCw, Filter, FilterX, ArrowUpDown, Pencil, Trash2, Code2, FileJson, FileSearch, GitCompareArrows, Link2, ChevronDown, Copy, Maximize2, MapPin, Pin, Inbox } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../../../components/ui/context-menu';
import { ForeignKeyPopover } from './ForeignKeyPopover';
import { ExpandedValueDialog } from './ExpandedValueDialog';
import { useCellSelection, buildCopyText, buildCopyHtml, parseCellKey, isDialogOriginEvent } from '../../common/hooks/useCellSelection';
import { useSpatialCopyTransform } from '../../common/hooks/useSpatialCopyTransform';
import { writeRichToClipboard, columnValuesToCsvList } from '../../common/utils/clipboardFormatters';
import { writeClipboardText } from '../../../../lib/clipboard';
import { FIND_USAGES_MENU_LABEL } from '../../dependency-viewer/services/findUsagesMenu';
import { useCellAggregates } from '../../common/hooks/useCellAggregates';
import { CellAggregateBar } from '../../common/components/CellAggregateBar';
import type { TableExplorerColumn, PendingChange, PendingInsert } from '../../core/types/workspaceTypes';
import { lookupForeignKey, type ForeignKeyMap } from '../hooks/useTableMetadata';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { engineSupports } from '../../../../core/engines/engineCapabilities';
import { normalizeColumnName } from '../../common/utils/normalizeColumnName';
import { guessJsonColumn, isJsonClassifiedColumn, isStringDataType, looksLikeJsonString } from '../../common/utils/jsonDetection';
import { useMetaInfo } from '../../common/hooks/useMetaInfo';
import { useLookupLabelMaps } from '../../common/hooks/useLookupLabelMaps';
import { useLookupLabelSelfHeal } from '../../common/hooks/useLookupLabelSelfHeal';
import { useOnDemandLookupLabels } from '../../common/hooks/useOnDemandLookupLabels';
import { evaluateFilterGroupsAgainstRow } from '../../common/utils/filterEvaluator';
import type { LooseRelationship } from '../../common/services/metaInfoApi';
import { categorizeJsonColumn, type JsonColumnInput } from '../../common/utils/jsonColumnCategorization';
import { buildDismissedEnumKeySet, categorizeEnumColumn, fkColumnSuggestsEnum, isEnumDeclarableType, nameSuggestsEnum, type EnumColumnInput } from '../../common/utils/enumColumnCategorization';
import { resolveLookupEnumTarget } from '../../common/utils/lookupEnumTarget';
import {
  isOnDemandLookupColumn,
  resolveEffectiveLookupDeclaration,
} from '../../common/utils/effectiveLookupDeclaration';
import type { EnumLookupTarget } from '@/types/electron';
import {
  categorizeSpatialColumn,
  sniffSpatialBlobColumns,
  findSpatialPairPartner,
  suggestSpatialPairs,
  sampleDeclarableTextColumns,
  type SpatialColumnInput,
} from '../../common/utils/spatialColumnCategorization';
import { EnumHeaderGlyph } from '../../common/components/grid-glyphs/EnumHeaderGlyph';
import { JsonHeaderGlyph } from '../../common/components/grid-glyphs/JsonHeaderGlyph';
import { SpatialHeaderGlyph, type SpatialGlyphState } from '../../common/components/grid-glyphs/SpatialHeaderGlyph';
import { resolveSpatialCellInput, type ExpandedSpatialPayload, type SpatialAccessorContext, type SpatialColumnDeclaration } from '../../common/utils/spatialValueAccessor';
import { SpatialCompareDialog } from '../../spatial/compare/SpatialCompareDialog';
import { useJsonStructureAggregatorStore } from '../../common/stores/jsonStructureAggregatorStore';
import { useJsonStructureFilteredAggregatorStore } from '../../common/stores/jsonStructureFilteredAggregatorStore';
import {
  formatValueForExpandedEdit,
  classifyExpandIconColumn,
  shouldShowExpandIconForClass,
  detectContentMode,
  type ExpandedEditMode,
} from '../utils/expandedEditDetection';
import { useColumnPinning, pinnedColumnBorderStyle } from '../../common/hooks/useColumnPinning';
import { trimGapSkeletonWindow } from '../../common/hooks/gapSkeletonWindow';
import { safeToISOString } from '../../../../utils/dateUtils';
import { ColumnProfileBadge } from '../../common/components/ColumnProfileBadge';
import type { ColumnProfileStats } from '../../common/utils/columnProfiling';
import { SkeletonRows } from '../../common/components/SkeletonRow';
import { openDeclareJsonColumnDialog } from '../../common/components/filters/declareJsonColumnDialogStore';
import { openDeclareEnumColumnDialog } from '../../common/components/filters/declareEnumColumnDialogStore';
import {
  openDeclareSpatialColumnDialog,
  type DeclareSpatialColumnDialogTrigger,
} from '../../common/components/filters/declareSpatialColumnDialogStore';
import { openEnumDetailsDialog } from '../../common/components/meta-info/enumDetailsDialogStore';
import { openJsonInspectorDialog } from '../../common/components/meta-info/jsonInspectorDialogStore';
import { TableExplorerGridRow, type TableExplorerRowCtx } from './TableExplorerGridRow';
import type {
  TableExplorerCellValueCodec,
  TableExplorerHostLookupDeclaration,
  TableExplorerMetadataEditorProviders,
  TableExplorerRelationshipPort,
} from './tableExplorerHostPorts';
import { CELL_EXPAND_OVERLAY_BUTTON_CLASS } from '../../common/components/cells/cellExpandButton';
import { openLooseRelationshipEditorDialog } from '../../common/components/filters/looseRelationshipEditorDialogStore';
import { isLooseFkCandidateType } from '../../common/utils/inferLooseRelationship';
import { serializePrimaryKey } from '../utils/serializePrimaryKey';
import { isNumericColumn, shouldGroupNumericColumn, formatNumericDisplay } from '../../common/utils/columnAlignment';
import { useAnalytics } from '../../../../core/analytics';
import { useAppStore } from '../../../../store/appStore';
import { NumericGroupingMenu } from '../../common/components/NumericGroupingMenu';

const EMPTY_PROFILING_ROWS: Record<string, unknown>[] = [];
// Stable default-prop values. Inline `= []` defaults allocate a fresh array
// per render, which changes the identity of every useCallback depending on
// them and defeats the memoized rows below.
const EMPTY_FILTERED_COLUMNS: string[] = [];
const EMPTY_SORTED_COLUMNS: string[] = [];
const EMPTY_PENDING_CHANGES: PendingChange[] = [];
const EMPTY_PRIMARY_KEY_COLUMNS: string[] = [];
const EMPTY_INSERTED_ROWS: PendingInsert[] = [];

/** State for the expanded value dialog (JSON/multiline text). */
export type TableExplorerExpandedEditState = {
  rowIndex: number;
  columnId: string;
  value: unknown;
  mode: ExpandedEditMode;
  primaryKey: Record<string, unknown>;
  isInsertedRow: boolean;
  tempId?: string;
  /** Whether the dialog should open in read-only mode */
  readOnly: boolean;
  /** Whether edit is allowed (for showing Edit button in read-only mode) */
  canEdit: boolean;
  /** T11 — present when opened from a spatial cell; drives the map pane. */
  spatial?: ExpandedSpatialPayload;
};

/**
 * The ONE seam that lets a non-SQL surface reuse this grid (spec §6.4).
 *
 * Every field's DEFAULT is today's behaviour, and the prop itself is optional,
 * so a SQL caller that passes no `host` renders byte-for-byte what it rendered
 * before this type existed. S1 declares it; S4 (Azure Storage table entities /
 * queue messages) fills it.
 */
export type TableExplorerGridHost = {
  /** messages: index; entities: PK (PartitionKey, RowKey). Omitted host = index. */
  rowIdentity: 'primary-key' | 'index';
  /** false: no "Open in query editor" / "Find usages" / "Open in new explorer". */
  sqlActions: boolean;
  /** false: no FK popover, no loose-FK editor, no lookup-label resolution. */
  relationshipNavigation: boolean;
  /** true: a MISSING key renders ∅, distinct from an explicit NULL (Table storage has no NULL). */
  absentValueGlyph: boolean;
  /** entities: PartitionKey/RowKey/Timestamp read-only. An extra veto, never a widening. */
  cellEditability?: (column: TableExplorerColumn, row: Record<string, unknown>) => boolean;
  /** entities: `storageMetaInfoScope(ref)` — never hand-built (F1). */
  metaInfoScope?: { database: string; schema: string; table: string };
  // S5 — non-SQL hosts bind row actions to the selected row object immediately.
  onSingleRowSelection?: (row: Record<string, unknown> | null) => void;
  /** Changes only when the host's row set changes; display-only row updates must preserve grid interaction state. */
  rowSetVersion?: string | number;
  relationships?: TableExplorerRelationshipPort;
  cellValueCodec?: TableExplorerCellValueCodec;
  metadataEditors?: TableExplorerMetadataEditorProviders;
  /** entities: a note on a pending inserted row (by temp id) — "may already exist" (S4 review H, H1). Omitted = none. */
  insertedRowNote?: (tempId: string) => string | undefined;
  /**
   * Presentation-only text for a plain cell (C2 N11: an Azure Table DateTime shows the storage surfaces' local date-time
   * instead of `2026-09-24T00:00:00.0000000Z`); `undefined` = the value as today. The cell's title, its editor, every
   * copy and export keep the RAW value (as PP-01's numeric grouping does). SQL hosts supply none, so the SQL Table
   * Explorer and the Query Editor results grid render exactly as before.
   */
  displayText?: (value: unknown, columnName: string, row: Record<string, unknown>) => string | undefined;
};

/**
 * Row identity for the TanStack table. The SQL default is the loaded-window
 * index (`String(startIndex + index)`) and stays that way for every omitted
 * host; `rowIdentity: 'primary-key'` keys by an ordered, collision-safe tuple
 * of the PK values instead, falling back to the index when the row carries no
 * usable PK so two PK-less rows can never collapse into one id.
 */
export function buildTableExplorerRowId(
  host: TableExplorerGridHost | undefined,
  primaryKeyColumns: string[],
): (row: Record<string, unknown>, index: number, startIndex: number) => string {
  if (host?.rowIdentity !== 'primary-key' || primaryKeyColumns.length === 0) {
    return (_row, index, startIndex) => String(startIndex + index);
  }
  return (row, index, startIndex) => {
    const parts: string[] = [];
    for (const key of primaryKeyColumns) {
      const value = row[key];
      if (value === undefined || value === null) return String(startIndex + index);
      parts.push(String(value));
    }
    return JSON.stringify(parts);
  };
}

type TableExplorerGridProps = {
  rows: Record<string, unknown>[];
  columns: TableExplorerColumn[];
  selectedColumns: string[] | '*';
  /** Called when the user drags a header to reorder columns; receives the new visible-column order. */
  onColumnReorder?: (newOrder: string[]) => void;
  isLoading: boolean;
  error?: string;
  onRefresh: () => void;
  /** Selected row index (for row highlight + side panel) */
  selectedRowIndex?: number | null;
  onRowSelect?: (args: { rowIndex: number; row: Record<string, unknown> }) => void;
  /**
   * Every row the cell selection spans (Ctrl-click, Shift range, drag), in grid order — the rows a host's
   * "Selected" export writes (Azure Table, S4 review F, F4). Absent = SQL = no selection reporting at all.
   */
  onSelectedRowsChange?: (rows: Record<string, unknown>[]) => void;
  onAddFilter?: (columnName: string) => void;
  onAddFilterWithValue?: (columnName: string, value: unknown) => void;
  onAddSort?: (columnName: string) => void;
  // Columns that already have filters/sorts (to disable menu items)
  filteredColumns?: string[];
  sortedColumns?: string[];
  // FK exploration support
  foreignKeyMap?: ForeignKeyMap;
  connectionId?: string;
  database?: string;
  /** Source schema for the explored table (used by FK popover for loose-relationship cleanup). */
  sourceSchemaForTable?: string;
  /** Source table name for the explored table (used by FK popover for loose-relationship cleanup). */
  sourceTableForTable?: string;
  /** Database engine — used to categorize JSON columns (native vs declared vs eligible). */
  engine?: DatabaseEngineId;
  // Edit mode support (Full Edit Mode)
  isEditMode?: boolean;
  pendingChanges?: PendingChange[];
  primaryKeyColumns?: string[];
  insertedRows?: PendingInsert[];
  onCellEdit?: (primaryKey: Record<string, unknown>, field: string, oldValue: unknown, newValue: unknown) => void;
  onInsertCellEdit?: (tempId: string, field: string, value: unknown) => void;
  onDeleteRow?: (primaryKey: Record<string, unknown>, row: Record<string, unknown>) => void;
  onUndoDelete?: (primaryKey: Record<string, unknown>) => void;
  onRemoveInsert?: (tempId: string) => void;
  // Quick Edit support (single cell edit via context menu)
  onQuickEdit?: (primaryKey: Record<string, unknown>, field: string, oldValue: unknown, newValue: unknown) => void;
  /** Whether quick edit is available (requires PK) */
  canQuickEdit?: boolean;
  /** Currently expanded row index for row details panel */
  expandedRowDetailsIndex?: number | null;
  /** Toggle row details panel for a given row */
  onToggleRowDetails?: (args: { rowIndex: number; row: Record<string, unknown> }) => void;
  /** LOB column names (Oracle CLOB/NCLOB/BLOB/BFILE). BLOB hex previews are bounded, and on a windowed page every text cell longer than 1 KB is clipped — `onFetchFullCellValue` fetches the full value. */
  lobColumns?: string[];
  /** Fetch full cell value for a LOB column (Oracle CLOB/NCLOB/BLOB/BFILE) */
  onFetchFullCellValue?: (columnName: string, primaryKey: Record<string, unknown>) => Promise<unknown>;
  /** Zoom level for the grid table (1 = 100%) */
  zoomLevel?: number;
  /** Per-column profile stats; when provided + showColumnProfiling, renders a badge under each header. */
  columnProfiles?: Map<string, ColumnProfileStats>;
  /** Whether the column profiling badge is shown under each column header. */
  showColumnProfiling?: boolean;
  /** Optional handler — opens the Data Profile workspace tab focused on the given column. */
  onProfileColumn?: (columnName: string) => void;
  /**
   * Optional handler — when provided, renders the "Find usages…" entry in the
   * HEADER context menu. Parity pair: `GridHeaderRow` (the Query Editor results
   * grid) must carry the same item.
   */
  onFindUsages?: (columnName: string) => void;
  /**
   * Slice 1 (FK Breadcrumb) — opens the Single-Row Details View for the
   * given row. Surfaced as the "Open record details" item in the row
   * (cell) context menu. Receives the row's primary-key columns + the row
   * object so the caller can build a `SingleRowNodeRef`.
   */
  onOpenRecordDetails?: (args: { row: Record<string, unknown> }) => void;
  /**
   * Slice 3 (FK Breadcrumb / EP2) — sibling items on the row context menu
   * for "Open in new explorer" (new TE tab on this row) + "Open in query
   * editor" (new QE tab with `SELECT * WHERE pk = ...`).
   */
  onOpenRowInNewExplorer?: (args: { row: Record<string, unknown> }) => void;
  onOpenRowInQueryEditor?: (args: { row: Record<string, unknown> }) => void;
  /**
   * Signature of the tab's ACTIVE filters (filterSignature(effectiveFilters)),
   * or null when unfiltered. When set, passive JSON samples route into the
   * ephemeral filtered aggregate instead of the whole-column aggregator —
   * filtered browsing must not skew the persisted structure cache (this also
   * fixes a pre-existing prevalence-pollution bug; see
   * Docs/JsonDiscriminatedStructure.md).
   */
  activeFilterSignature?: string | null;
  /**
   * S1 Task 1.17 — host seam (spec §6.4). Omitted = today's SQL behaviour for
   * every caller; a storage host (S4) narrows identity, SQL affordances,
   * relationship navigation, editability and the MetaInfo slots.
   */
  host?: TableExplorerGridHost;
};

// 8 rows of overscan (was 15): with memoized rows the window refills one row
// per boundary crossing during ordinary scrolling, and the fast-scroll gap
// branch covers flicks — the remaining role of overscan is hiding the refill
// of a few rows per wheel notch, which 8 covers with margin. On 74-column
// tables the cut saves ~1,000 off-screen cells per full-window mount
// (measured ~25-30% off page-jump mounts).
const DEFAULT_OVERSCAN = 8;
const MIN_COLUMN_WIDTH = 50;
const MAX_AUTO_COLUMN_WIDTH = 400;
const ROW_HEIGHT = 32;

// Stable dnd-kit sensor options — see the `dndSensors` comment below.
const DND_POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };
const DND_KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };

/**
 * Render-prop wrapper that exposes `useSortable` bindings for a single header
 * cell. Keeps the existing `<th>` JSX inline (with its many closure deps) while
 * still allowing drag-to-reorder on each header.
 */
function SortableHeaderBindings({
  id,
  disabled,
  children,
}: {
  id: string;
  disabled?: boolean;
  children: (bindings: {
    setNodeRef: (node: HTMLElement | null) => void;
    attributes: React.HTMLAttributes<HTMLElement>;
    listeners: React.DOMAttributes<HTMLElement> | undefined;
    style: React.CSSProperties;
    isDragging: boolean;
  }) => React.ReactElement;
}) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = buildSortableHeaderStyle(transform, transition, isDragging);

  return children({
    setNodeRef,
    attributes: attributes as React.HTMLAttributes<HTMLElement>,
    listeners: listeners as React.DOMAttributes<HTMLElement> | undefined,
    style,
    isDragging,
  });
}

export const TableExplorerGrid = React.memo(function TableExplorerGrid({
  rows,
  columns,
  selectedColumns,
  onColumnReorder,
  isLoading,
  error,
  onRefresh,
  selectedRowIndex = null,
  onRowSelect,
  onSelectedRowsChange,
  onAddFilter,
  onAddFilterWithValue,
  onAddSort,
  filteredColumns = EMPTY_FILTERED_COLUMNS,
  sortedColumns = EMPTY_SORTED_COLUMNS,
  foreignKeyMap: foreignKeyMapProp,
  connectionId,
  database: databaseProp,
  sourceSchemaForTable: sourceSchemaForTableProp,
  sourceTableForTable: sourceTableForTableProp,
  engine,
  isEditMode = false,
  pendingChanges = EMPTY_PENDING_CHANGES,
  primaryKeyColumns = EMPTY_PRIMARY_KEY_COLUMNS,
  insertedRows = EMPTY_INSERTED_ROWS,
  onCellEdit,
  onInsertCellEdit,
  onDeleteRow,
  onUndoDelete,
  onRemoveInsert,
  onQuickEdit,
  canQuickEdit = false,
  expandedRowDetailsIndex = null,
  onToggleRowDetails,
  lobColumns,
  onFetchFullCellValue,
  zoomLevel = 1,
  columnProfiles,
  showColumnProfiling = false,
  onProfileColumn,
  onFindUsages: onFindUsagesProp,
  onOpenRecordDetails,
  onOpenRowInNewExplorer: onOpenRowInNewExplorerProp,
  onOpenRowInQueryEditor: onOpenRowInQueryEditorProp,
  activeFilterSignature = null,
  host,
}: TableExplorerGridProps) {
  // ── Host seam (S1 Task 1.17) ────────────────────────────────────────────
  // Resolved ONCE here by shadowing the prop names, so every consumer below
  // (including the memo dep arrays and the child components) picks up the
  // effective value without the 3k lines underneath changing shape. With no
  // `host` every binding is the prop itself — identical to before.
  //
  // `??`, never `||`: an empty schema is legitimate for a SQL host (SQLite
  // passes `''`) and must survive. Storage no longer passes one — ruling P2
  // (b) re-pinned `storageMetaInfoScope` to the non-empty
  // `STORAGE_TABLE_METAINFO_SCHEMA` so the loose-FK matcher below (which
  // returns early on an empty schema) works for Table storage entities.
  const hostSqlActions = host?.sqlActions ?? true;
  const hostRelationshipNavigation = host?.relationshipNavigation ?? true;
  const hostAbsentValueGlyph = host?.absentValueGlyph ?? false;
  const database = host?.metaInfoScope?.database ?? databaseProp;
  const sourceSchemaForTable = host?.metaInfoScope?.schema ?? sourceSchemaForTableProp;
  const sourceTableForTable = host?.metaInfoScope?.table ?? sourceTableForTableProp;
  // Relationship navigation off = no FK model at all, so no popover, no loose-FK
  // candidates and no lookup-label work — the UI is hidden AND the lookups never run.
  const foreignKeyMap = hostRelationshipNavigation ? foreignKeyMapProp : undefined;
  const onFindUsages = hostSqlActions ? onFindUsagesProp : undefined;
  const onOpenRowInNewExplorer = hostSqlActions ? onOpenRowInNewExplorerProp : undefined;
  const onOpenRowInQueryEditor = hostSqlActions ? onOpenRowInQueryEditorProp : undefined;

  const { track } = useAnalytics();
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;
  const [containerHeight, setContainerHeight] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [isFixedLayout, setIsFixedLayout] = useState(false);
  // Track quick edit state: { rowIndex, columnId, originalValue, primaryKey }
  const [quickEditCell, setQuickEditCell] = useState<{
    rowIndex: number;
    columnId: string;
    originalValue: unknown;
    primaryKey: Record<string, unknown>;
  } | null>(null);
  // Track full edit mode cell being edited: { rowIndex, columnId }
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    columnId: string;
  } | null>(null);
  // Cell the ONE delegated context menu is open on (see the ContextMenu around
  // <tbody> below) — replaces the per-cell Radix menu that used to dominate
  // scroll-render cost on wide tables.
  const [menuCell, setMenuCell] = useState<{ rowIndex: number; columnId: string } | null>(null);
  // State for expanded value dialog (JSON/multiline text)
  const [expandedEditState, setExpandedEditState] = useState<TableExplorerExpandedEditState | null>(null);

  const columnHelper = useMemo(() => createColumnHelper<Record<string, unknown>>(), []);

  const sortedColumnSet = useMemo(() => {
    return new Set(sortedColumns.map(normalizeColumnName));
  }, [sortedColumns]);

  // Filter columns based on selectedColumns
  const visibleColumns = useMemo(() => {
    if (selectedColumns === '*') {
      return columns;
    }
    // Preserve the order from selectedColumns
    return selectedColumns
      .map((colName) => columns.find((col) => col.name === colName))
      .filter((col): col is TableExplorerColumn => col !== undefined);
  }, [columns, selectedColumns]);

  // Normalize columns with FK info from metadata
  const normalizedColumns = useMemo(() => {
    return visibleColumns.map((col, index) => {
      // By the exact column name: `"ref id"` never takes `"ref-id"`'s FK.
      const fkInfo = lookupForeignKey(foreignKeyMap, col.name);
      return {
        id: col.name || `col_${index}`,
        key: col.name,
        name: col.name || `Column ${index + 1}`,
        dataType: col.dataType,
        isPrimaryKey: col.isPrimaryKey,
        isForeignKey: col.isForeignKey || !!fkInfo,
        isIdentity: col.isIdentity || false,
        nullable: col.nullable,
        fkInfo,
        // E1 parity: carry the schema JSON classification through so the
        // grid's JSON-detection paths can become metadata-driven (matching
        // ResultsGrid). Purely additive — no current consumer reads this
        // field yet, so rendering is unchanged.
        jsonClassification: col.jsonClassification,
        isNativeEnumType: col.isNativeEnumType,
      };
    });
  }, [visibleColumns, foreignKeyMap]);

  const columnDefById = useMemo(() => {
    const map = new Map<string, (typeof normalizedColumns)[number]>();
    for (const column of normalizedColumns) {
      map.set(column.id, column);
    }
    return map;
  }, [normalizedColumns]);

  // Host seam (S1 Task 1.17): the host's `cellEditability` is an EXTRA veto on
  // top of the grid's own rules (identity never editable, PK only on inserted
  // rows) — it can never widen them. The host contract is typed against the
  // caller's own `TableExplorerColumn`, so the original column is handed back,
  // not the grid's normalized shape.
  const hostColumnById = useMemo(() => {
    const map = new Map<string, TableExplorerColumn>();
    visibleColumns.forEach((column, index) => map.set(column.name || `col_${index}`, column));
    return map;
  }, [visibleColumns]);

  const hostCellEditability = host?.cellEditability;
  const isHostCellEditable = useCallback(
    (columnId: string, row: Record<string, unknown>): boolean => {
      if (!hostCellEditability) return true;
      const column = hostColumnById.get(columnId);
      return column ? hostCellEditability(column, row) : true;
    },
    [hostCellEditability, hostColumnById],
  );

  // Per-column expand-icon classification — hoisted out of the per-cell path:
  // classifying the dataType per cell (regex parses inside
  // `shouldShowExpandIcon`) was a measured fast-scroll hotspot on wide tables.
  const expandIconClassById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof classifyExpandIconColumn>>();
    for (const column of normalizedColumns) {
      map.set(column.id, classifyExpandIconColumn({
        name: column.name,
        dataType: column.dataType,
        nullable: column.nullable ?? true,
        isPrimaryKey: column.isPrimaryKey,
        isForeignKey: column.isForeignKey,
      }));
    }
    return map;
  }, [normalizedColumns]);

  // PP-01 — numeric alignment sets. `numericColumnIds` drives right alignment
  // (header label, cells and the NULL marker); `groupedNumericColumnIds` is the
  // quantity-only subset that also gets thousands grouping in the user's
  // `numericGrouping` mode (empty when the mode is `off`, so no cell ever
  // calls the formatter). Display only — every copy/export path reads the raw
  // row value, never the rendered text. Mirrored in
  // `common/components/ResultsGrid.tsx`. The mode is read ONCE here and handed
  // down through the row ctx — never subscribed per cell.
  const numericGrouping = useAppStore((state) => state.numericGrouping);
  const { numericColumnIds, groupedNumericColumnIds } = useMemo(() => {
    const numeric = new Set<string>();
    const grouped = new Set<string>();
    for (const column of normalizedColumns) {
      if (!isNumericColumn(column)) continue;
      numeric.add(column.id);
      if (numericGrouping !== 'off' && shouldGroupNumericColumn(column)) grouped.add(column.id);
    }
    return { numericColumnIds: numeric, groupedNumericColumnIds: grouped };
  }, [normalizedColumns, numericGrouping]);

  // MetaInfo drives both loose-FK matching (per cell) and JSON-column
  // categorization (native vs declared vs eligible) further down.
  const { data: metaInfo } = useMetaInfo(connectionId ?? null, database ?? null);

  // Per-column candidate loose rels (not yet filtered by sourceRow — that's done per-row inline).
  const looseRelsByColumn = useMemo((): Map<string, LooseRelationship[]> => {
    const map = new Map<string, LooseRelationship[]>();
    if (!hostRelationshipNavigation) return map;
    if (!sourceSchemaForTable || !sourceTableForTable) return map;
    for (const rel of (metaInfo?.looseRelationships ?? [])) {
      if (rel.fromSchema === sourceSchemaForTable && rel.fromTable === sourceTableForTable) {
        const existing = map.get(rel.fromColumn) ?? [];
        existing.push(rel);
        map.set(rel.fromColumn, existing);
      }
    }
    return map;
  }, [metaInfo, sourceSchemaForTable, sourceTableForTable, hostRelationshipNavigation]);

  // The lookup-enum resolver's per-column target — one unconditional FK target
  // (real, or a single unfiltered non-path loose rel), or absent. Shared by the
  // declarable set, the hint's FK arm (both read membership only) and
  // `useLookupLabelMaps`, which needs the target itself to resolve implicit
  // lookup enums via `referenceTables`. Declared HERE, above the label-map
  // hook that consumes it, even though its other two consumers sit further
  // down with the rest of the enum id sets. Mirrors ResultsGrid.
  const lookupTargetResolutions = useMemo(() => {
    const resolutions = new Map<string, ReturnType<typeof resolveLookupEnumTarget>>();
    for (const column of normalizedColumns) {
      resolutions.set(column.id, resolveLookupEnumTarget({
        dataType: column.dataType,
        realFk:
          column.fkInfo && !column.fkInfo.isLoose
            ? {
                referencedSchema: column.fkInfo.referencedSchema,
                referencedTable: column.fkInfo.referencedTable,
                referencedColumn: column.fkInfo.referencedColumn,
              }
            : undefined,
        looseRels: looseRelsByColumn.get(column.name) ?? [],
      }));
    }
    return resolutions;
  }, [normalizedColumns, looseRelsByColumn]);

  const fkLookupTargets = useMemo(() => {
    const targets = new Map<string, EnumLookupTarget>();
    for (const column of normalizedColumns) {
      const { target } = lookupTargetResolutions.get(column.id) ?? { target: null };
      if (target) targets.set(column.id, target);
    }
    return targets;
  }, [normalizedColumns, lookupTargetResolutions]);

  const hostLookupDeclarations = useMemo(() => {
    const declarations = new Map<string, TableExplorerHostLookupDeclaration>();
    if (!host?.relationships) return declarations;
    for (const column of normalizedColumns) {
      const resolution = lookupTargetResolutions.get(column.id);
      if (!resolution?.target?.relationshipId) continue;
      const targetFilter = 'targetFilter' in resolution ? resolution.targetFilter : undefined;
      const pairsScope = host.relationships.lookupPairsScope?.({ declarationId: resolution.target.relationshipId, lookup: resolution.target, targetFilter });
      declarations.set(column.id, {
        lookup: resolution.target,
        lookupMode: 'on-demand',
        targetFilter,
        declarationId: resolution.target.relationshipId,
        ...(pairsScope ? { pairsScope } : {}),
      });
    }
    return declarations;
  }, [host?.relationships, lookupTargetResolutions, normalizedColumns]);

  // Lookup-enum labels for FK cells (`3 · Active`). Table Explorer columns
  // carry their source identity at TABLE level, so the hook's per-column
  // identity is rebuilt here. Mirrored in `common/components/ResultsGrid.tsx`.
  const lookupLabelColumns = useMemo(
    () =>
      normalizedColumns.map((c) => ({
        id: c.id,
        key: c.key,
        sourceSchema: sourceSchemaForTable,
        sourceTable: sourceTableForTable,
        sourceColumn: c.name,
      })),
    [normalizedColumns, sourceSchemaForTable, sourceTableForTable],
  );
  const cachedLookupLabelInfos = useLookupLabelMaps({
    metaInfo,
    columns: lookupLabelColumns,
    fkLookupTargets,
    hostLookupDeclarations,
  });

  // Cell selection + aggregate computation
  const cellSelection = useCellSelection({
    columnIds: normalizedColumns.map((c) => c.id),
    disabled: isEditMode,
    rootRef: containerRef,
  });

  // ── Selection reads for memoized rows (ref-backed) ──────────────────────
  // The selection Set changes identity on EVERY mousemove of a drag-select;
  // if the row ctx carried `isCellSelected`/`handleCellMouseDown` directly,
  // each drag step would rebuild the ctx and re-render every visible row
  // (~50 x 74 cells). Rows instead get identity-STABLE trampolines that read
  // the latest selection through a ref, plus a per-row `selectionSignature`
  // prop (below) that re-renders exactly the rows whose own membership
  // changed.
  const cellSelectionRef = useRef(cellSelection);
  cellSelectionRef.current = cellSelection;
  const isCellSelectedStable = useCallback(
    (rowIndex: number, columnId: string) => cellSelectionRef.current.isCellSelected(rowIndex, columnId),
    []
  );
  const onCellMouseDownStable = useCallback(
    (rowIndex: number, columnId: string, event: React.MouseEvent) =>
      cellSelectionRef.current.handleCellMouseDown(rowIndex, columnId, event),
    []
  );
  const onCellMouseEnterStable = useCallback(
    (rowIndex: number, columnId: string) => cellSelectionRef.current.handleCellMouseEnter(rowIndex, columnId),
    []
  );
  // Per-row digest of selected columns ("" = none). Row memo comparators
  // compare this string, so a drag re-renders only rows whose membership
  // changed (typically the rows the drag boundary crossed).
  const selectionSignatureByRow = useMemo(() => {
    if (cellSelection.selectedCells.size === 0) return null;
    const byRow = new Map<number, string[]>();
    for (const key of cellSelection.selectedCells) {
      const { rowIndex, columnId } = parseCellKey(key);
      const cols = byRow.get(rowIndex);
      if (cols) cols.push(columnId);
      else byRow.set(rowIndex, [columnId]);
    }
    const signatures = new Map<number, string>();
    for (const [rowIndex, cols] of byRow) {
      // NUL separator: column ids are raw names/aliases, which may contain ','.
      signatures.set(rowIndex, cols.sort().join('\u0000'));
    }
    return signatures;
  }, [cellSelection.selectedCells]);

  // S5 host seam: report USER selection changes only. Rows/host are read through refs so a
  // row-set change never re-derives a target from a stale index, and the row-set reset
  // below (which clears the cells) is not reported as a deselection — the host keeps its
  // identity-bound target (and can highlight it through `selectedRowIndex`).
  const singleRowRowsRef = useRef(rows);
  singleRowRowsRef.current = rows;
  const onSingleRowSelectionRef = useRef(host?.onSingleRowSelection);
  onSingleRowSelectionRef.current = host?.onSingleRowSelection;
  const rowSetResetClearingRef = useRef(false);
  useEffect(() => {
    const report = onSingleRowSelectionRef.current;
    if (!report) return;
    if (rowSetResetClearingRef.current) { rowSetResetClearingRef.current = false; return; }
    const selectedRows = new Set<number>();
    for (const key of cellSelection.selectedCells) selectedRows.add(parseCellKey(key).rowIndex);
    const rowIndex = selectedRows.size === 1 ? selectedRows.values().next().value : undefined;
    report(typeof rowIndex === 'number' ? singleRowRowsRef.current[rowIndex] ?? null : null);
  }, [cellSelection.selectedCells]);
  // Escape is a user deselection even when the cells are already empty (a row-set reset kept
  // the host target armed): `clearSelection` then keeps the same Set and the effect above never
  // runs, so report it here. A non-empty selection is still reported once, by that effect.
  const selectedCellsRef = useRef(cellSelection.selectedCells);
  selectedCellsRef.current = cellSelection.selectedCells;
  const hasSingleRowHost = Boolean(host?.onSingleRowSelection);
  useEffect(() => {
    if (!hasSingleRowHost) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // An Escape inside a dialog (e.g. the queue Update dialog) closes that dialog, not the grid target.
      if (event.key === 'Escape' && !isDialogOriginEvent(event, containerRef.current) && selectedCellsRef.current.size === 0) onSingleRowSelectionRef.current?.(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hasSingleRowHost]);
  // F4 (S4 review F): the rows the selection spans, for a host that asks. SQL grids pass no callback.
  useEffect(() => {
    if (!onSelectedRowsChange) return;
    const indexes = new Set<number>();
    for (const key of cellSelection.selectedCells) indexes.add(parseCellKey(key).rowIndex);
    onSelectedRowsChange([...indexes].sort((a, b) => a - b).flatMap((index) => rows[index] ? [rows[index]] : []));
  }, [cellSelection.selectedCells, rows, onSelectedRowsChange]);

  const cellAggregates = useCellAggregates({
    selectedCells: cellSelection.selectedCells,
    sortedResults: rows,
    columns: normalizedColumns,
  });

  // Native spatial columns → gray ⌖ glyph + sniffer-derived cell summary.
  // Gated by the spatial capability so kusto opts out entirely. SQLite unions
  // in a BLOB/affinity-less value sniff over the loaded window (zero I/O) since
  // GeoPackage/SpatiaLite columns are declared BLOB and miss name-based
  // categorization. metaInfo is threaded for T18.
  // Computed here (moved up, T5) — the Ctrl+C effect below needs it declared
  // first for `useSpatialCopyTransform`'s WKT copy transform.
  const nativeSpatialColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!engine || !sourceTableForTable) return ids;
    if (!engineSupports(engine, 'spatial', 'supportsSpatialTypes')) return ids;
    for (const column of normalizedColumns) {
      const input: SpatialColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
      };
      if (categorizeSpatialColumn(input, metaInfo) === 'native-spatial') {
        ids.add(column.id);
      }
    }
    if (engine === 'sqlite') {
      const blobInputs: SpatialColumnInput[] = normalizedColumns.map((column) => ({
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
      }));
      const promoted = sniffSpatialBlobColumns(blobInputs, rows.slice(0, 20));
      for (const column of normalizedColumns) {
        if (promoted.has(column.name)) ids.add(column.id);
      }
    }
    return ids;
  }, [engine, metaInfo, normalizedColumns, sourceSchemaForTable, sourceTableForTable, rows]);

  // T18 — user-declared coordinate columns (any engine, even without native
  // spatial types). Maps the grid columnId → declaration (a pair keys only the
  // LAT column). `declarableSpatialColumnIds` gates the "Declare as
  // coordinates…" header entry on non-native, non-declared columns.
  const declaredSpatialColumns = useMemo(() => {
    const map = new Map<string, SpatialColumnDeclaration>();
    if (!metaInfo || !sourceTableForTable) return map;
    for (const column of normalizedColumns) {
      if (nativeSpatialColumnIds.has(column.id)) continue;
      const decl = metaInfo.spatialColumns.find(
        (d) =>
          d.schema === (sourceSchemaForTable ?? '') &&
          d.table === sourceTableForTable &&
          d.column === column.name,
      );
      if (decl) map.set(column.id, decl);
    }
    return map;
  }, [metaInfo, normalizedColumns, nativeSpatialColumnIds, sourceSchemaForTable, sourceTableForTable]);

  const declaredSpatialColumnIds = useMemo(
    () => new Set(declaredSpatialColumns.keys()),
    [declaredSpatialColumns],
  );

  // T19 — declarable suggestion heuristic (two arms, spec §3.4): a name-only
  // WKT/GeoJSON-text arm (`categorizeSpatialColumn`'s own `spatial-declarable`
  // category), a lat/lng pair NAME arm (`suggestSpatialPairs`, zero I/O), and a
  // TEXT-column VALUE-SAMPLING arm over the already-loaded window
  // (`sampleDeclarableTextColumns`, zero EXTRA I/O — never queries the DB).
  // Replaces the pre-T19 "every non-native, non-declared column" placeholder.
  const declarableSpatialColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!engine || !sourceTableForTable) return ids;
    const candidates: SpatialColumnInput[] = [];
    const idByName = new Map<string, string>();
    for (const column of normalizedColumns) {
      if (nativeSpatialColumnIds.has(column.id)) continue;
      if (declaredSpatialColumnIds.has(column.id)) continue;
      const input: SpatialColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
      };
      candidates.push(input);
      idByName.set(column.name, column.id);
      if (categorizeSpatialColumn(input, metaInfo) === 'spatial-declarable') {
        ids.add(column.id);
      }
    }
    for (const name of suggestSpatialPairs(candidates)) {
      const id = idByName.get(name);
      if (id) ids.add(id);
    }
    for (const name of sampleDeclarableTextColumns(candidates, rows.slice(0, 20))) {
      const id = idByName.get(name);
      if (id) ids.add(id);
    }
    return ids;
  }, [engine, normalizedColumns, nativeSpatialColumnIds, declaredSpatialColumnIds, sourceTableForTable, sourceSchemaForTable, metaInfo, rows]);

  // T5 — WKT copy/export parity: shared transform for every copy seam below
  // (Ctrl+C, context menu "Copy value", header "Copy values").
  const spatialCopyTransform = useSpatialCopyTransform(
    nativeSpatialColumnIds,
    engine ?? null,
    (columnId) => normalizedColumns.find((c) => c.id === columnId)?.dataType,
    declaredSpatialColumns,
  );

  // Ctrl+C: copy selected cell values
  useEffect(() => {
    if (cellSelection.selectedCells.size === 0) return;

    const colKeyMap = new Map(normalizedColumns.map((c) => [c.id, c.key]));
    const colNames = new Map(normalizedColumns.map((c) => [c.id, c.name]));
    const columnIds = normalizedColumns.map((c) => c.id);

    const onKeyDown = (e: KeyboardEvent) => {
      // PB2 fix: `> 0` (not `> 1`) so a single selected cell copies too — matches
      // ResultsGrid's guard exactly. `buildCopyText`/`buildCopyHtml` already handle
      // size-1 selections as a raw single-value paste (see `buildCopyHtml`'s
      // `size < 2` early return), so no naive flip risk here.
      if ((e.metaKey || e.ctrlKey) && e.key === 'c' && cellSelection.selectedCells.size > 0) {
        // Skip when the keystroke originates inside a dialog/editor/input — let it
        // handle its own copy (e.g. Monaco inside ExpandedValueDialog).
        const target = e.target as HTMLElement | null;
        if (target?.closest('[role="dialog"], .monaco-editor, input, textarea, [contenteditable="true"]')) {
          return;
        }
        e.preventDefault();
        const text = buildCopyText(cellSelection.selectedCells, rows, columnIds, colKeyMap, 0, spatialCopyTransform);
        const html = buildCopyHtml(cellSelection.selectedCells, rows, columnIds, colNames, colKeyMap, 0, spatialCopyTransform);
        void writeRichToClipboard(text, html);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [cellSelection.selectedCells, rows, normalizedColumns, spatialCopyTransform]);

  // Native JSON columns + user-declared JSON columns. These render as JSON
  // cells regardless of value-shape heuristics (a declared column whose value
  // doesn't parse falls back to the "!" glyph below).
  const forcedJsonColumnIds = useMemo(() => {
    const ids = new Set<string>();
    // E1 column-metadata path (two-grids parity with ResultsGrid): an explicit
    // non-`not-json` schema classification (`native-jsonb` / `native-json`)
    // authoritatively forces JSON cell rendering regardless of engine,
    // source-table metadata, value size, or the value-shape heuristic. Runs
    // BEFORE the engine/source-table early-return so a metadata-classified
    // column is never dropped just because those props aren't set.
    for (const column of normalizedColumns) {
      if (isJsonClassifiedColumn(column.jsonClassification)) {
        ids.add(column.id);
      }
    }
    if (!engine || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      // Mirrors ResultsGrid: metadata-classified columns already in `ids`
      // skip the heuristic pass (precedence parity, not just a perf guard).
      if (ids.has(column.id)) continue;
      const input: JsonColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
      };
      const cat = categorizeJsonColumn(input, metaInfo);
      if (cat === 'native-json' || cat === 'declared-json') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [engine, metaInfo, normalizedColumns, sourceSchemaForTable, sourceTableForTable]);

  // Subset of forced JSON ids that are user-declared on a text column. Drives
  // the "!" glyph fallback in the cell when parse fails.
  const declaredJsonColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!engine || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      const input: JsonColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
      };
      if (categorizeJsonColumn(input, metaInfo) === 'declared-json') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [engine, metaInfo, normalizedColumns, sourceSchemaForTable, sourceTableForTable]);

  const nativeJsonColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!engine || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      const input: JsonColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
      };
      if (categorizeJsonColumn(input, metaInfo) === 'native-json') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [engine, metaInfo, normalizedColumns, sourceSchemaForTable, sourceTableForTable]);

  const jsonColumnIdSet = useMemo(() => {
    const jsonColumns = new Set<string>(forcedJsonColumnIds);

    for (const column of normalizedColumns) {
      if (jsonColumns.has(column.id)) continue;
      if (!isStringDataType(column.dataType)) continue;
      if (guessJsonColumn(rows, column.key, { maxParseAttempts: 8, minSuccessfulParses: 2 })) {
        jsonColumns.add(column.id);
      }
    }

    return jsonColumns;
  }, [forcedJsonColumnIds, normalizedColumns, rows]);

  // Enum-column categorization — three Sets mirror the JSON glyph trio.
  // Declared trumps native; native covers MySQL ENUM + Postgres user-defined
  // enums; eligible covers short string types + name-hinted int columns.
  const declaredEnumColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!engine || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      const input: EnumColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
        isNativeEnumType: column.isNativeEnumType,
      };
      if (categorizeEnumColumn(input, metaInfo) === 'declared-enum') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [engine, metaInfo, normalizedColumns, sourceSchemaForTable, sourceTableForTable]);

  // R4 — columns with NO declaration of their own whose FK target is a table
  // the user flagged in `referenceTables`. "Mark `dbo.Statuses` once and every
  // column referencing it shows names": an implicit lookup enum is
  // declared-LIKE everywhere (blue `▾`, "Enum details", labelled cell editor),
  // never hint-like. Resolved through the shared
  // `resolveEffectiveLookupDeclaration` seam so it can't drift from the label
  // maps / picker / editor. Mirrors ResultsGrid.
  //
  // `'cached'` mode ONLY. An on-demand target's pairs are never enumerable —
  // the whole point is that the table is too big — so the surfaces this set
  // feeds (the blue `▾`, "Enum details", the enum cell editor, the enum filter
  // picker) would all be offering a list that cannot be built. Those columns
  // get labels and nothing else; labels and glyphs are driven by two different
  // sets on purpose (design § "Deliberate exclusions").
  const implicitLookupColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if ((!metaInfo && hostLookupDeclarations.size === 0) || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      const effective = resolveEffectiveLookupDeclaration({
        metaInfo,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        column: column.name,
        resolvedTarget: fkLookupTargets.get(column.id),
        hostDeclaration: hostLookupDeclarations.get(column.id),
      });
      if (effective?.kind === 'implicit' && effective.mode === 'cached') ids.add(column.id);
    }
    return ids;
  }, [metaInfo, normalizedColumns, sourceSchemaForTable, sourceTableForTable, fkLookupTargets, hostLookupDeclarations]);

  /**
   * Columns whose pairs are obtained on demand — the implicit ones excluded
   * above PLUS the explicitly declared ones pointing at the same kind of
   * target. An explicit declaration says what the column IS; it never says how
   * a table too big to enumerate gives up its pairs, so it cannot buy back the
   * dropdown-shaped surfaces (B-1). Subtracted from the declared-LIKE union
   * below; the declaration itself stays on disk and wins again the moment the
   * flag goes back to `'cached'`. Mirrors ResultsGrid.
   */
  const onDemandLookupColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if ((!metaInfo && hostLookupDeclarations.size === 0) || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      const onDemand = isOnDemandLookupColumn({
        metaInfo,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        column: column.name,
        resolvedTarget: fkLookupTargets.get(column.id),
        hostDeclaration: hostLookupDeclarations.get(column.id),
      });
      if (onDemand) ids.add(column.id);
    }
    return ids;
  }, [metaInfo, normalizedColumns, sourceSchemaForTable, sourceTableForTable, fkLookupTargets, hostLookupDeclarations]);

  /**
   * (Declared ∪ implicit) − on-demand — the set every "this column IS an enum"
   * surface reads (header glyph, header context menu, cell-edit variant).
   * `declaredEnumColumnIds` itself stays literal so nothing that means "has a
   * declaration record" drifts.
   * The union is what preserves mutual exclusivity for free: the glyph and menu
   * chains already test declared FIRST, so an implicit column can never also
   * render the `▾?` hint or the "Declare as enum" entry.
   */
  const enumDeclaredLikeColumnIds = useMemo(() => {
    if (implicitLookupColumnIds.size === 0 && onDemandLookupColumnIds.size === 0) {
      return declaredEnumColumnIds;
    }
    const ids = new Set([...declaredEnumColumnIds, ...implicitLookupColumnIds]);
    for (const id of onDemandLookupColumnIds) ids.delete(id);
    return ids;
  }, [declaredEnumColumnIds, implicitLookupColumnIds, onDemandLookupColumnIds]);

  const nativeEnumColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!engine || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      const input: EnumColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
        isNativeEnumType: column.isNativeEnumType,
      };
      if (categorizeEnumColumn(input, metaInfo) === 'native-enum') {
        ids.add(column.id);
      }
    }
    return ids;
  }, [engine, metaInfo, normalizedColumns, sourceSchemaForTable, sourceTableForTable]);

  // Two distinct sets:
  //  • `declarableEnumColumnIds` — *any* sensibly-typed column (short text or
  //    integer), regardless of name, PLUS any column with one unconditional FK
  //    target (a lookup-enum candidate, whatever its type). Drives the
  //    right-click "Declare as enum" context-menu entry: the explicit
  //    affordance must be reachable on any column where declaring an enum
  //    makes sense — and at least as broad as the `▾?` glyph.
  //  • `hintedEnumColumnIds` — the subset whose *name* also hints categorical
  //    (status / type / role / …). Drives only the always-visible inline `▾?`
  //    glyph, so the proactive marker stays rare (enum analogue of
  //    `declarableJsonColumnIds` gating JSON's `{}?`).
  // Native / declared columns are handled by their own sets and always show a
  // glyph regardless of name.
  //
  // `fkLookupTargets` (hoisted above `useLookupLabelMaps`) is the resolver's
  // per-column target.
  const declarableEnumColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!engine || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      const input: EnumColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
        isNativeEnumType: column.isNativeEnumType,
      };
      if (isEnumDeclarableType(input) || fkLookupTargets.has(column.id)) {
        ids.add(column.id);
      }
    }
    return ids;
  }, [engine, normalizedColumns, sourceSchemaForTable, sourceTableForTable, fkLookupTargets]);

  const dismissedEnumKeys = useMemo(
    () => buildDismissedEnumKeySet(metaInfo),
    // buildDismissedEnumKeySet reads only metaInfo.enumGlyphDismissals; depending
    // on the whole metaInfo object would needlessly recompute on unrelated
    // metaInfo changes. Narrow dep is intentional and correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metaInfo?.enumGlyphDismissals],
  );

  const hintedEnumColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!engine || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      const input: EnumColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
        isNativeEnumType: column.isNativeEnumType,
      };
      if (
        dismissedEnumKeys.has(
          `${sourceSchemaForTable ?? ''}.${sourceTableForTable}.${column.name}`,
        )
      )
        continue;
      if (!nameSuggestsEnum(column.name)) continue;
      if (categorizeEnumColumn(input, metaInfo) === 'enum-eligible') {
        ids.add(column.id);
        continue;
      }
      // FK arm — a categorically-named column with one unconditional FK target
      // is hinted even when its type alone isn't eligible. Same resolution
      // `handleEnumGlyphClick` performs, so the glyph that appears here
      // always opens in lookup mode.
      if (fkColumnSuggestsEnum(input, metaInfo, fkLookupTargets.has(column.id))) {
        ids.add(column.id);
      }
    }
    return ids;
  }, [
    engine,
    metaInfo,
    normalizedColumns,
    sourceSchemaForTable,
    sourceTableForTable,
    dismissedEnumKeys,
    fkLookupTargets,
  ]);

  // Columns that look like JSON but haven't been declared yet — drives the
  // "Declare as JSON" context-menu item on the column header and individual
  // cells. Restricted to `json-eligible` columns so we don't offer the
  // affordance on, say, a 100-char varchar status column.
  const declarableJsonColumnIds = useMemo(() => {
    const ids = new Set<string>();
    if (!engine || !sourceTableForTable) return ids;
    for (const column of normalizedColumns) {
      if (forcedJsonColumnIds.has(column.id)) continue;
      const input: JsonColumnInput = {
        engine,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType,
      };
      const cat = categorizeJsonColumn(input, metaInfo);
      if (cat !== 'json-eligible') continue;
      if (!guessJsonColumn(rows, column.key, { maxParseAttempts: 8, minSuccessfulParses: 2 })) continue;
      ids.add(column.id);
    }
    return ids;
  }, [engine, metaInfo, normalizedColumns, sourceSchemaForTable, sourceTableForTable, forcedJsonColumnIds, rows]);

  const handleDeclareAsJson = useCallback(
    (columnName: string, source: 'header-menu' | 'cell-menu') => {
      if (!engine || !connectionId || !database || !sourceTableForTable) return;
      const column = normalizedColumns.find((c) => c.name === columnName);
      if (!column) return;
      const sampleValues = rows.map((row) => row[column.key]);
      openDeclareJsonColumnDialog({
        connectionId,
        database,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        column: column.name,
        dataType: column.dataType,
        engine,
        sampleValues,
        source,
      });
    },
    [engine, connectionId, database, sourceTableForTable, sourceSchemaForTable, normalizedColumns, rows],
  );

  const handleShowJsonDetails = useCallback(
    (columnName: string, source: 'header-menu' | 'cell-menu') => {
      if (!engine || !connectionId || !database || !sourceTableForTable) return;
      const column = normalizedColumns.find((c) => c.name === columnName);
      if (!column) return;
      openJsonInspectorDialog({
        connectionId,
        database,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        column: column.name,
        engine,
        dataType: column.dataType,
        source,
      });
    },
    [engine, connectionId, database, sourceTableForTable, sourceSchemaForTable, normalizedColumns],
  );

  const handleDeclaredGlyphClick = useCallback((columnId: string) => {
    const column = normalizedColumns.find((c) => c.id === columnId);
    if (!column || !engine || !connectionId || !database || !sourceTableForTable) return;
    track('feature:json_glyph_click', { source: 'declared', surface: 'table_explorer' });
    openJsonInspectorDialog({
      connectionId,
      database,
      schema: sourceSchemaForTable ?? '',
      table: sourceTableForTable,
      column: column.name,
      engine,
      dataType: column.dataType,
      source: 'header-glyph',
    });
  }, [normalizedColumns, engine, connectionId, database, sourceSchemaForTable, sourceTableForTable, track]);

  const handleLooseFkGlyphClick = useCallback((columnId: string) => {
    const column = normalizedColumns.find((c) => c.id === columnId);
    if (!column || !connectionId || !database || !sourceTableForTable) return;
    // First matching loose FK on the source triple — the editor opens
    // pre-filled. Multi-FK source columns surface additional declarations
    // via the dialog's "Add another" button.
    const rel = (metaInfo?.looseRelationships ?? []).find(
      (r) =>
        r.fromSchema === (sourceSchemaForTable ?? '') &&
        r.fromTable === sourceTableForTable &&
        r.fromColumn === column.name,
    );
    if (!rel) return;
    track('feature:loose_fk_header_glyph_click', { surface: 'table_explorer' });
    openLooseRelationshipEditorDialog({
      connectionId,
      database,
      engine,
      source: {
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        name: column.name,
        dataType: column.dataType ?? '',
      },
      existingRelationshipId: rel.id,
      source_label: 'header_glyph',
      metadataEditors: host?.metadataEditors,
    });
  }, [normalizedColumns, connectionId, database, engine, sourceSchemaForTable, sourceTableForTable, metaInfo, track, host?.metadataEditors]);

  const handleEditLooseFkFromMenu = useCallback(
    (columnName: string, surface: 'header-menu' | 'cell-menu') => {
      if (!connectionId || !database || !sourceTableForTable) return;
      const column = normalizedColumns.find((c) => c.name === columnName);
      if (!column) return;
      const rel = (metaInfo?.looseRelationships ?? []).find(
        (r) =>
          r.fromSchema === (sourceSchemaForTable ?? '') &&
          r.fromTable === sourceTableForTable &&
          r.fromColumn === column.name,
      );
      if (!rel) return;
      track('feature:loose_fk_context_menu_edit', {
        surface: 'table_explorer',
        from: surface,
      });
      openLooseRelationshipEditorDialog({
        connectionId,
        database,
        engine,
        source: {
          schema: sourceSchemaForTable ?? '',
          table: sourceTableForTable,
          name: column.name,
          dataType: column.dataType ?? '',
        },
        existingRelationshipId: rel.id,
        source_label: `context_menu_${surface === 'header-menu' ? 'header' : 'cell'}`,
        metadataEditors: host?.metadataEditors,
      });
    },
    [normalizedColumns, connectionId, database, engine, sourceSchemaForTable, sourceTableForTable, metaInfo, track, host?.metadataEditors],
  );

  const handleDeclareLooseFkFromMenu = useCallback(
    (columnName: string, surface: 'header-menu' | 'cell-menu') => {
      if (!connectionId || !database || !sourceTableForTable) return;
      const column = normalizedColumns.find((c) => c.name === columnName);
      if (!column) return;
      track('feature:loose_fk_context_menu_declare', {
        surface: 'table_explorer',
        from: surface,
      });
      openLooseRelationshipEditorDialog({
        connectionId,
        database,
        engine,
        source: {
          schema: sourceSchemaForTable ?? '',
          table: sourceTableForTable,
          name: column.name,
          dataType: column.dataType ?? '',
        },
        source_label: `context_menu_${surface === 'header-menu' ? 'header' : 'cell'}_declare`,
        metadataEditors: host?.metadataEditors,
      });
    },
    [normalizedColumns, connectionId, database, engine, sourceSchemaForTable, sourceTableForTable, track, host?.metadataEditors],
  );

  const handleNativeOrDeclarableGlyphClick = useCallback(
    (columnId: string, source: 'native' | 'eligible') => {
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column || !engine || !connectionId || !database || !sourceTableForTable) return;
      track('feature:json_glyph_click', { source, surface: 'table_explorer' });
      const sampleValues = rows.map((row) => row[column.key]);
      openDeclareJsonColumnDialog({
        connectionId,
        database,
        schema: sourceSchemaForTable ?? '',
        table: sourceTableForTable,
        column: column.name,
        dataType: column.dataType,
        engine,
        sampleValues,
        source: 'header-glyph',
        mode: source === 'native' ? 'native' : 'eligible',
      });
    },
    [normalizedColumns, engine, connectionId, database, sourceSchemaForTable, sourceTableForTable, rows, track],
  );

  const handleEnumGlyphClick = useCallback(
    (columnId: string, mode: 'native' | 'eligible' | 'declared') => {
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column || !engine || !connectionId || !database || !sourceTableForTable) return;
      track('feature:enum_glyph_click', { source: mode, surface: 'table_explorer' });
      const schema = sourceSchemaForTable ?? '';
      if (mode === 'declared') {
        const decl = metaInfo?.enumColumns.find(
          (d) =>
            d.schema === schema &&
            d.table === sourceTableForTable &&
            d.column === column.name,
        );
        // An IMPLICIT lookup enum has no declaration record at all — the blue
        // glyph it renders still has to open details. `'lookup'` is the only
        // source it can have (R5 gives the dialog its implicit variant).
        if (!decl && !implicitLookupColumnIds.has(columnId)) return;
        openEnumDetailsDialog({
          connectionId,
          database,
          schema,
          table: sourceTableForTable,
          column: column.name,
          engine,
          declaredSource: decl?.source ?? 'lookup',
          source: 'header-glyph',
          // Host metadata provider (Azure Table, S4 review D, D5): a lookup refresh reads the label column through it.
          ...(host?.metadataEditors ? { metadataEditors: host.metadataEditors } : {}),
        });
        return;
      }
      // FK-backed columns can become *lookup* enums — values and names read
      // from the referenced table. Only the "declare" path resolves this; the
      // native-enum path keeps its own (type-driven) mode. A reason other than
      // 'none' means the column HAS FK context we can't use, and the dialog
      // explains why in its plain mode.
      const resolution =
        mode === 'eligible'
          ? resolveLookupEnumTarget({
              dataType: column.dataType,
              realFk:
                column.fkInfo && !column.fkInfo.isLoose
                  ? {
                      referencedSchema: column.fkInfo.referencedSchema,
                      referencedTable: column.fkInfo.referencedTable,
                      referencedColumn: column.fkInfo.referencedColumn,
                    }
                  : undefined,
              looseRels: looseRelsByColumn.get(column.name) ?? [],
            })
          : null;
      const lookupTarget = resolution?.target ?? undefined;
      const lookupIneligibleReason =
        resolution && resolution.target === null && resolution.reason !== 'none'
          ? resolution.reason
          : undefined;
      openDeclareEnumColumnDialog({
        connectionId,
        database,
        schema,
        table: sourceTableForTable,
        column: column.name,
        dataType: column.dataType,
        engine,
        mode: lookupTarget ? 'lookup' : mode === 'native' ? 'native' : 'eligible',
        trigger: 'header-glyph',
        ...(lookupTarget ? { lookupTarget } : {}),
        ...(lookupIneligibleReason ? { lookupIneligibleReason } : {}),
        // Host metadata providers (Azure Table): the lookup label column is
        // read through them, never through SQL metadata (S4 review C, M7).
        ...(host?.metadataEditors ? { metadataEditors: host.metadataEditors } : {}),
      });
    },
    [normalizedColumns, engine, connectionId, database, sourceSchemaForTable, sourceTableForTable, metaInfo, looseRelsByColumn, track, implicitLookupColumnIds, host?.metadataEditors],
  );

  // T18 — open the declare / details dialog for a coordinate column. `state`
  // distinguishes an already-declared column (details/remove) from a declarable
  // one (declare form).
  const handleSpatialColumnAction = useCallback(
    (columnId: string, state: 'declared' | 'declarable', triggerOverride?: DeclareSpatialColumnDialogTrigger) => {
      const column = normalizedColumns.find((c) => c.id === columnId);
      if (!column || !engine || !connectionId || !database || !sourceTableForTable) return;
      const schema = sourceSchemaForTable ?? '';
      const siblingColumnsRaw = normalizedColumns
        .filter((c) => c.id !== columnId)
        .map((c) => c.name);
      // T19 — when the column was suggested via the pair NAME arm (e.g.
      // `pickup_lat`), put its best-guess LNG partner first so the dialog's
      // default "Longitude column" selection (`siblingColumns[0]`) is right,
      // not whatever column happens to be first in the table.
      const partner = state === 'declarable' ? findSpatialPairPartner(column.name, siblingColumnsRaw) : null;
      const siblingColumns = partner
        ? [partner, ...siblingColumnsRaw.filter((c) => c !== partner)]
        : siblingColumnsRaw;
      openDeclareSpatialColumnDialog({
        connectionId,
        database,
        schema,
        table: sourceTableForTable,
        column: column.name,
        dataType: column.dataType,
        engine,
        trigger: triggerOverride ?? (state === 'declared' ? 'glyph' : 'header-menu'),
        siblingColumns,
        existing: state === 'declared' ? declaredSpatialColumns.get(columnId) : undefined,
      });
    },
    [normalizedColumns, engine, connectionId, database, sourceSchemaForTable, sourceTableForTable, declaredSpatialColumns],
  );

  // Spatial header glyph. A native glyph records intent (the map peek is the
  // cell-level interaction); a declared glyph (T18) opens the coordinate
  // details/remove dialog. Analytics mirror the JSON/enum glyph events.
  const handleSpatialGlyphClick = useCallback(
    (columnId: string, state: SpatialGlyphState) => {
      track('feature:spatial_glyph_click', { source: state, surface: 'table_explorer' });
      if (state === 'declared') handleSpatialColumnAction(columnId, 'declared');
      // T19 — the `⌖?` glyph opens the declare dialog pre-filled, mirroring the
      // enum `hintedEnumColumnIds` glyph flow. Explicit 'glyph' trigger (rather
      // than the header-menu default) so `feature:spatial_declare`'s trigger
      // prop correctly attributes this NEW entry point.
      else if (state === 'declarable') handleSpatialColumnAction(columnId, 'declarable', 'glyph');
    },
    [track, handleSpatialColumnAction],
  );

  // Slice 5 — Cache hydration: feed parsed values for known-JSON columns
  // (forced or value-detected) into the session-scoped aggregator. Runs only
  // when `rows`/columns change, not per cell render. The aggregator caps at
  // 1000 samples per column and debounces its flush to MetaInfo.
  const pushJsonSample = useJsonStructureAggregatorStore((s) => s.pushSample);
  const pushFilteredJsonSample = useJsonStructureFilteredAggregatorStore((s) => s.pushFilteredSample);
  useEffect(() => {
    if (!connectionId || !database || !sourceTableForTable) return;
    if (jsonColumnIdSet.size === 0 || rows.length === 0) return;
    const schema = sourceSchemaForTable ?? '';
    const sampleColumns = normalizedColumns.filter((c) => jsonColumnIdSet.has(c.id));
    for (const col of sampleColumns) {
      for (const row of rows) {
        const v = row[col.key];
        if (v === null || v === undefined) continue;
        let parsed: unknown = v;
        if (typeof v === 'string') {
          if (!looksLikeJsonString(v)) continue;
          try {
            parsed = JSON.parse(v);
          } catch {
            continue;
          }
        } else if (typeof v !== 'object') {
          continue;
        }
        const columnKeyForCol = {
          connectionId,
          database,
          schema,
          table: sourceTableForTable,
          column: col.name,
        };
        if (activeFilterSignature) {
          pushFilteredJsonSample(columnKeyForCol, activeFilterSignature, parsed);
        } else {
          pushJsonSample(columnKeyForCol, parsed);
        }
      }
    }
  }, [
    rows,
    jsonColumnIdSet,
    normalizedColumns,
    connectionId,
    database,
    sourceSchemaForTable,
    sourceTableForTable,
    activeFilterSignature,
    pushJsonSample,
    pushFilteredJsonSample,
  ]);

  // Flush on window blur and on tab unmount. Both are best-effort; the
  // aggregator stays dirty if a flush fails so the next trigger retries.
  // We coalesce repeats with a short cooldown so unmount-while-blurred
  // doesn't fire two back-to-back flushes.
  const flushAllJsonAggregates = useJsonStructureAggregatorStore(
    (s) => s.flushAll,
  );
  useEffect(() => {
    let lastFlushAt = 0;
    const FLUSH_COOLDOWN_MS = 250;
    const flushIfStale = () => {
      const now = Date.now();
      if (now - lastFlushAt < FLUSH_COOLDOWN_MS) return;
      lastFlushAt = now;
      void flushAllJsonAggregates();
    };
    window.addEventListener('blur', flushIfStale);
    return () => {
      window.removeEventListener('blur', flushIfStale);
      flushIfStale();
    };
  }, [flushAllJsonAggregates]);

  // Edit mode helpers
  const getPrimaryKeyFromRow = useCallback(
    (row: Record<string, unknown>): Record<string, unknown> => {
      const rowKeyByNormalized = new Map<string, string>();
      for (const key of Object.keys(row)) {
        rowKeyByNormalized.set(normalizeColumnName(key), key);
      }

      const pk: Record<string, unknown> = {};
      for (const col of primaryKeyColumns) {
        const rowKey = rowKeyByNormalized.get(normalizeColumnName(col));
        pk[col] = rowKey ? row[rowKey] : row[col];
      }
      return pk;
    },
    [primaryKeyColumns]
  );

  // T30 — two-cell geometry compare eligibility: exactly two cells selected, BOTH resolving
  // through resolveSpatialCellInput (native or T18-declared). Any two rows/columns, same grid.
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const spatialCompareCandidate = useMemo(() => {
    if (cellSelection.selectedCells.size !== 2 || !engine) return null;
    if (nativeSpatialColumnIds.size === 0 && declaredSpatialColumnIds.size === 0) return null;
    const [keyA, keyB] = [...cellSelection.selectedCells];
    const coordA = parseCellKey(keyA);
    const coordB = parseCellKey(keyB);
    const rowDataA = rows[coordA.rowIndex];
    const rowDataB = rows[coordB.rowIndex];
    const ctx: SpatialAccessorContext = {
      engine,
      nativeSpatialColumnIds,
      columnDataTypes: new Map(normalizedColumns.map((c) => [c.id, c.dataType ?? ''])),
      declaredSpatialColumns,
    };
    const inputA = resolveSpatialCellInput(coordA.columnId, rowDataA, ctx);
    const inputB = resolveSpatialCellInput(coordB.columnId, rowDataB, ctx);
    if (!inputA || !inputB) return null;

    // LOB spatial cells hand the compare dialog a bounded full-value fetch (by PK) — same
    // contract as the single-cell peek/expanded map pane above.
    const fetchFullValueFor = (rowData: Record<string, unknown> | undefined, columnId: string) => {
      if (!rowData) return undefined;
      const pk = getPrimaryKeyFromRow(rowData);
      return lobColumns?.includes(columnId) && onFetchFullCellValue && typeof rowData[columnId] === 'string' && Object.keys(pk).length > 0
        ? () => onFetchFullCellValue(columnId, pk)
        : undefined;
    };

    return {
      a: { ...coordA, input: inputA, fetchFullValue: fetchFullValueFor(rowDataA, coordA.columnId) },
      b: { ...coordB, input: inputB, fetchFullValue: fetchFullValueFor(rowDataB, coordB.columnId) },
    };
  }, [
    cellSelection.selectedCells, engine, nativeSpatialColumnIds, declaredSpatialColumnIds,
    declaredSpatialColumns, rows, normalizedColumns, getPrimaryKeyFromRow, lobColumns, onFetchFullCellValue,
  ]);

  // ── Pending-change lookups, indexed by primary key ─────────────────────
  // The old shape linearly scanned `pendingChanges` AND rebuilt the row's
  // normalized PK map for EVERY cell (`getPrimaryKeyFromRow` normalizes all
  // ~N row keys), which made scrolling in edit mode with pending changes a
  // per-cell hotspot (~N^2 normalizations per mounting row). The index below
  // is built once per `pendingChanges` change; rows resolve their pending
  // state ONCE (one PK computation, one Map hit) and cells read from it.
  //
  // Key serialization semantics (order-independence, 1 vs '1', BigInt,
  // structural Dates): see `serializePrimaryKey`.

  type RowPendingState = { deleted: boolean; updates: Map<string, unknown> };
  const pendingByPk = useMemo(() => {
    if (pendingChanges.length === 0) return null;
    const index = new Map<string, RowPendingState>();
    for (const change of pendingChanges) {
      if (change.type !== 'update' && change.type !== 'delete') continue;
      const key = serializePrimaryKey(change.primaryKey);
      let state = index.get(key);
      if (!state) {
        state = { deleted: false, updates: new Map() };
        index.set(key, state);
      }
      if (change.type === 'delete') state.deleted = true;
      else state.updates.set(change.field, change.newValue);
    }
    return index;
  }, [pendingChanges]);

  /** Resolve a row's pending state with ONE PK computation (null = nothing pending). */
  const getRowPendingState = useCallback(
    (row: Record<string, unknown>): RowPendingState | null => {
      if (!pendingByPk) return null;
      return pendingByPk.get(serializePrimaryKey(getPrimaryKeyFromRow(row))) ?? null;
    },
    [pendingByPk, getPrimaryKeyFromRow]
  );

  const isRowDeleted = useCallback(
    (row: Record<string, unknown>): boolean => getRowPendingState(row)?.deleted ?? false,
    [getRowPendingState]
  );

  const getCellPendingValue = useCallback(
    (row: Record<string, unknown>, field: string): { hasPending: boolean; value?: unknown } => {
      const state = getRowPendingState(row);
      if (!state || !state.updates.has(field)) return { hasPending: false };
      return { hasPending: true, value: state.updates.get(field) };
    },
    [getRowPendingState]
  );

  // Quick edit handlers
  const handleStartQuickEdit = useCallback(
    (rowIndex: number, columnId: string, originalValue: unknown, row: Record<string, unknown>) => {
      const primaryKey = getPrimaryKeyFromRow(row);
      setQuickEditCell({ rowIndex, columnId, originalValue, primaryKey });
    },
    [getPrimaryKeyFromRow]
  );

  const handleQuickEditConfirm = useCallback(
    (newValue: unknown) => {
      if (quickEditCell && onQuickEdit) {
        onQuickEdit(quickEditCell.primaryKey, quickEditCell.columnId, quickEditCell.originalValue, newValue);
      }
      setQuickEditCell(null);
    },
    [quickEditCell, onQuickEdit]
  );

  const handleQuickEditCancel = useCallback(() => {
    setQuickEditCell(null);
  }, []);

  // Expanded edit handlers (for JSON/multiline text)
  const handleStartExpandedEdit = useCallback(
    async (
      rowIndex: number,
      columnId: string,
      value: unknown,
      mode: ExpandedEditMode,
      row: Record<string, unknown>,
      options?: { readOnly?: boolean; canEdit?: boolean }
    ) => {
      const isInsertedRow = '_tempId' in row;
      const isPKColumn = primaryKeyColumns.includes(columnId);
      const pk = isInsertedRow ? {} : getPrimaryKeyFromRow(row);

      // For LOB columns, fetch full value from backend before opening dialog
      let expandValue = value;
      if (lobColumns?.includes(columnId) && !isInsertedRow && onFetchFullCellValue && Object.keys(pk).length > 0) {
        try {
          const fullValue = await onFetchFullCellValue(columnId, pk);
          if (fullValue !== undefined) expandValue = fullValue;
        } catch {
          // Fall back to truncated preview value
        }
      }

      // Determine read-only and can-edit states
      // - In edit mode: always editable (unless it's a PK column)
      // - Not in edit mode: read-only, but can edit if quick edit is available
      const effectiveReadOnly = options?.readOnly ?? !isEditMode;
      const effectiveCanEdit = options?.canEdit ?? (
        isEditMode
          ? !isPKColumn
          : (canQuickEdit && !isPKColumn && !isInsertedRow)
      );

      setExpandedEditState({
        rowIndex,
        columnId,
        value: expandValue,
        mode,
        primaryKey: pk,
        isInsertedRow,
        tempId: isInsertedRow ? (row._tempId as string) : undefined,
        readOnly: effectiveReadOnly,
        canEdit: effectiveCanEdit,
      });
    },
    [getPrimaryKeyFromRow, isEditMode, canQuickEdit, primaryKeyColumns, lobColumns, onFetchFullCellValue]
  );

  const handleExpandedEditConfirm = useCallback(
    (newValue: string) => {
      if (!expandedEditState) return;

      const { columnId, value: oldValue, isInsertedRow, tempId, primaryKey } = expandedEditState;
      // Convert empty string to null
      const finalValue = newValue === '' ? null : newValue;

      // Close the expanded edit dialog FIRST
      setExpandedEditState(null);

      // For edit mode operations, execute immediately (no confirmation dialog)
      if (isInsertedRow && tempId && onInsertCellEdit) {
        onInsertCellEdit(tempId, columnId, finalValue);
      } else if (isEditMode && onCellEdit) {
        onCellEdit(primaryKey, columnId, oldValue, finalValue);
      } else if (onQuickEdit && !isEditMode) {
        // For quick edit (non-edit mode), delay opening the confirmation dialog
        // to allow the expanded edit dialog's backdrop to fully unmount
        setTimeout(() => {
          onQuickEdit(primaryKey, columnId, oldValue, finalValue);
        }, 250);
      }
    },
    [expandedEditState, isEditMode, onCellEdit, onInsertCellEdit, onQuickEdit]
  );

  // Full edit mode handlers
  const handleStartFullEdit = useCallback((rowIndex: number, columnId: string) => {
    if (!isEditMode) return;
    setEditingCell({ rowIndex, columnId });
  }, [isEditMode]);

  const handleFullEditConfirm = useCallback(
    (newValue: unknown, row: Record<string, unknown>, columnId: string) => {
      if (!editingCell) return;

      const isInsertedRow = '_tempId' in row;

      if (isInsertedRow && onInsertCellEdit) {
        const tempId = row._tempId as string;
        onInsertCellEdit(tempId, columnId, newValue);
      } else if (onCellEdit) {
        const primaryKey = getPrimaryKeyFromRow(row);
        const oldValue = row[columnId];
        onCellEdit(primaryKey, columnId, oldValue, newValue);
      }

      setEditingCell(null);
    },
    [editingCell, onCellEdit, onInsertCellEdit, getPrimaryKeyFromRow]
  );

  const handleFullEditCancel = useCallback(() => {
    setEditingCell(null);
  }, []);

  // Delete/undo row handlers for full edit mode
  const handleDeleteRow = useCallback(
    (row: Record<string, unknown>) => {
      if (!onDeleteRow) return;
      const primaryKey = getPrimaryKeyFromRow(row);
      onDeleteRow(primaryKey, row);
    },
    [onDeleteRow, getPrimaryKeyFromRow]
  );

  const handleUndoDeleteRow = useCallback(
    (row: Record<string, unknown>) => {
      if (!onUndoDelete) return;
      const primaryKey = getPrimaryKeyFromRow(row);
      onUndoDelete(primaryKey);
    },
    [onUndoDelete, getPrimaryKeyFromRow]
  );

  const handleRemoveInsertRow = useCallback(
    (tempId: string) => {
      if (!onRemoveInsert) return;
      onRemoveInsert(tempId);
    },
    [onRemoveInsert]
  );

  // Combine existing rows with inserted rows for display
  const allRows = useMemo(() => {
    if (insertedRows.length === 0) {
      return rows;
    }

    // First, all existing rows
    const combined = [...rows];
    // Then, add inserted rows at the end (with _tempId marker)
    for (const insert of insertedRows) {
      combined.push({ ...insert.row, _tempId: insert.tempId });
    }
    return combined;
  }, [rows, insertedRows]);

  const columnIds = useMemo(() => normalizedColumns.map((c) => c.id), [normalizedColumns]);
  const rowsForProfiling = showColumnProfiling ? rows : EMPTY_PROFILING_ROWS;
  const {
    columnPinning,
    onColumnPinningChange,
    pinColumn,
    unpinColumn,
    isColumnPinned,
    getPinnedLeftOffset,
    getPinnedRightOffset,
    isLastPinnedLeft,
    isFirstPinnedRight,
  } = useColumnPinning({ columnIds });

  const columnDefs = useMemo<ColumnDef<Record<string, unknown>>[]>(() =>
    normalizedColumns.map((column) => {
      const isNumeric = numericColumnIds.has(column.id);
      const isGroupedNumeric = groupedNumericColumnIds.has(column.id);
      return columnHelper.accessor((row) => row[column.key], {
        id: column.id,
        header: () => (
          <div className={`flex flex-col leading-tight ${isNumeric ? 'items-end' : ''}`}>
            <span className="flex items-center gap-1">
              {column.isPrimaryKey && (
                <abbr
                  className="text-warning text-[10px] no-underline"
                  title="Primary Key"
                  aria-label="Primary Key"
                >
                  PK
                </abbr>
              )}
              {column.isForeignKey && (
                column.fkInfo?.isLoose ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLooseFkGlyphClick(column.id);
                    }}
                    className="text-teal-500 text-[10px] italic leading-none cursor-pointer hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                    title="Loose Foreign Key (user-declared) — click to edit"
                    aria-label="Loose Foreign Key (user-declared) — click to edit"
                    data-testid="grid-header-loose-fk-prefix"
                  >
                    FK
                  </button>
                ) : (
                  <abbr
                    className="text-blue-500 text-[10px] no-underline"
                    title="Foreign Key"
                    aria-label="Foreign Key"
                  >
                    FK
                  </abbr>
                )
              )}
              {declaredJsonColumnIds.has(column.id) ? (
                <JsonHeaderGlyph state="declared" onClick={() => handleDeclaredGlyphClick(column.id)} />
              ) : nativeJsonColumnIds.has(column.id) ? (
                <JsonHeaderGlyph state="native" onClick={() => handleNativeOrDeclarableGlyphClick(column.id, 'native')} />
              ) : declarableJsonColumnIds.has(column.id) ? (
                <JsonHeaderGlyph state="declarable" onClick={() => handleNativeOrDeclarableGlyphClick(column.id, 'eligible')} />
              ) : null}
              {/* Declared ∪ implicit — the blue `▾` is the glyph for both, and
                  testing it FIRST is what keeps the `▾?` hint off an implicit
                  column. */}
              {enumDeclaredLikeColumnIds.has(column.id) ? (
                <EnumHeaderGlyph state="declared" onClick={() => handleEnumGlyphClick(column.id, 'declared')} />
              ) : nativeEnumColumnIds.has(column.id) ? (
                <EnumHeaderGlyph state="native" onClick={() => handleEnumGlyphClick(column.id, 'native')} />
              ) : hintedEnumColumnIds.has(column.id) ? (
                <EnumHeaderGlyph state="eligible" onClick={() => handleEnumGlyphClick(column.id, 'eligible')} />
              ) : null}
              {nativeSpatialColumnIds.has(column.id) ? (
                <SpatialHeaderGlyph state="native" onClick={() => handleSpatialGlyphClick(column.id, 'native')} />
              ) : declaredSpatialColumnIds.has(column.id) ? (
                <SpatialHeaderGlyph state="declared" onClick={() => handleSpatialGlyphClick(column.id, 'declared')} />
              ) : declarableSpatialColumnIds.has(column.id) ? (
                <SpatialHeaderGlyph state="declarable" onClick={() => handleSpatialGlyphClick(column.id, 'declarable')} />
              ) : null}
              {isColumnPinned(column.id) && (
                <button
                  type="button"
                  className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground flex-shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  title="Unpin column"
                  aria-label="Unpin column"
                  onClick={(e) => {
                    e.stopPropagation();
                    unpinColumn(column.id);
                  }}
                >
                  <Pin className="h-3 w-3" aria-hidden="true" />
                </button>
              )}
              <span>{column.name}</span>
            </span>
            {showColumnProfiling && columnProfiles?.has(column.id) && (
              <ColumnProfileBadge
                stats={columnProfiles.get(column.id)!}
                rows={rowsForProfiling}
                onOpenFullProfile={
                  onProfileColumn ? () => onProfileColumn(column.name) : undefined
                }
              />
            )}
          </div>
        ),
        minSize: MIN_COLUMN_WIDTH,
        cell: ({ getValue, row }) => {
          const value = getValue();

          // NULL/undefined values
          if (value === null || value === undefined) {
            return <span className="text-muted-foreground/60 italic text-xs">NULL</span>;
          }

          // Object values — display Date and other non-plain objects as strings
          if (typeof value === 'object') {
            const isPlainObjOrArray = Array.isArray(value)
              || Object.getPrototypeOf(value) === Object.prototype
              || Object.getPrototypeOf(value) === null;
            if (isPlainObjOrArray) {
              return <span className="font-mono text-xs">{JSON.stringify(value)}</span>;
            }
            return <span className="text-xs">{value instanceof Date ? safeToISOString(value) : String(value)}</span>;
          }

          const rawDisplayValue = String(value);
          // PP-01 display grouping — presentation only, never the copy value.
          const displayValue = isGroupedNumeric
            ? formatNumericDisplay(value, numericGrouping) ?? rawDisplayValue
            : rawDisplayValue;

          // FK cell with popover for exploration
          if (column.fkInfo && connectionId && database) {
            const candidateRels = looseRelsByColumn.get(column.name) ?? [];
            const matchingLooseRels = row.original
              ? candidateRels.filter((r) => evaluateFilterGroupsAgainstRow(row.original, r.sourceFilter))
              : candidateRels;
            return (
              <ForeignKeyPopover
                connectionId={connectionId}
                database={database}
                referencedSchema={column.fkInfo.referencedSchema || ''}
                referencedTable={column.fkInfo.referencedTable}
                referencedColumn={column.fkInfo.referencedColumn}
                value={value}
                isLoose={column.fkInfo.isLoose}
                sourceSchema={sourceSchemaForTable}
                sourceTable={sourceTableForTable}
                sourceColumn={column.name}
                surface="table_explorer"
                sourceRow={row.original}
                matchingLooseRels={matchingLooseRels}
                relationshipPort={host?.relationships}
              >
                {displayValue}
              </ForeignKeyPopover>
            );
          }

          return displayValue;
        },
      });
    }),
    [columnHelper, normalizedColumns, numericColumnIds, groupedNumericColumnIds, numericGrouping, connectionId, database, isColumnPinned, unpinColumn, showColumnProfiling, columnProfiles, rowsForProfiling, sourceSchemaForTable, sourceTableForTable, looseRelsByColumn, declaredJsonColumnIds, nativeJsonColumnIds, declarableJsonColumnIds, handleDeclaredGlyphClick, handleNativeOrDeclarableGlyphClick, enumDeclaredLikeColumnIds, nativeEnumColumnIds, declarableEnumColumnIds, hintedEnumColumnIds, handleEnumGlyphClick, nativeSpatialColumnIds, declaredSpatialColumnIds, declarableSpatialColumnIds, handleSpatialGlyphClick, handleLooseFkGlyphClick, host?.relationships]
  );

  const columnResizeMode: ColumnResizeMode = 'onChange';
  const showRowDetailsExpander = !isEditMode && !!onToggleRowDetails;

  // DnD sensors for column reordering. PointerSensor with distance:5 means a
  // short click still passes through to the header's onClick (sort), and only
  // a >5px drag actually starts reordering.
  //
  // The options are module-level constants ON PURPOSE: dnd-kit's `useSensor`
  // memoizes on its options argument, so inline object literals made
  // `dndSensors` a NEW array on every render — which invalidated the memoized
  // `headerContent` below and re-rendered all 74 header cells (sortable
  // bindings + Radix menus) on every scroll tick (measured: the dominant
  // remaining fast-scroll cost after row memoization).
  const dndSensors = useSensors(
    useSensor(PointerSensor, DND_POINTER_SENSOR_OPTIONS),
    useSensor(KeyboardSensor, DND_KEYBOARD_SENSOR_OPTIONS)
  );

  const handleHeaderDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!onColumnReorder) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = normalizedColumns.map((c) => c.id);
      const oldIndex = ids.indexOf(active.id as string);
      const newIndex = ids.indexOf(over.id as string);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(ids, oldIndex, newIndex);
      onColumnReorder(reordered);
      track('feature:te_columns_reorder', { surface: 'grid' });
    },
    [normalizedColumns, onColumnReorder, track]
  );

  // Track container height for virtualization
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateSize = () => setContainerHeight(container.clientHeight);
    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  const rowSetResetToken = host?.rowSetVersion ?? rows;
  // Reset interaction state when the host says the row set changed. Non-host
  // SQL callers retain the existing rows-reference behaviour.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.scrollTop = 0;
    setScrollTop(0);
    setMenuCell(null);
    if (onSingleRowSelectionRef.current && cellSelection.selectedCells.size > 0) rowSetResetClearingRef.current = true;
    cellSelection.clearSelection();
  }, [rowSetResetToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll the externally-selected row into view when it lands outside the current
  // window (T14 map pane: clicking a feature selects its grid row, which may be
  // beyond the rendered buffer). No-op when the row is already visible, so ordinary
  // in-view row clicks never jump. Mirrors the QE ResultsGrid controlled-selection
  // scroll-into-view seam — TableExplorerGrid is a SEPARATE grid, so it needs its own.
  useEffect(() => {
    if (selectedRowIndex === null || selectedRowIndex === undefined) return;
    const container = containerRef.current;
    if (!container) return;
    const rowTop = selectedRowIndex * ROW_HEIGHT;
    const rowBottom = rowTop + ROW_HEIGHT;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;
    if (rowTop < viewTop) {
      container.scrollTop = rowTop;
    } else if (rowBottom > viewBottom) {
      container.scrollTop = rowBottom - container.clientHeight;
    }
  }, [selectedRowIndex]);

  // Refs + hide for the delegated expand affordance — declared above the
  // scroll handler that needs them; the rest of the mechanism (mouseover
  // delegation, click resolution, the overlay element) lives further down
  // next to the delegated context menu.
  const expandOverlayRef = useRef<HTMLButtonElement | null>(null);
  const expandOverlayTargetRef = useRef<{ rowIndex: number; columnId: string } | null>(null);

  const hideExpandOverlay = useCallback(() => {
    if (!expandOverlayTargetRef.current) return;
    expandOverlayTargetRef.current = null;
    const overlay = expandOverlayRef.current;
    if (overlay) overlay.style.display = 'none';
  }, []);

  const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const nextScrollTop = event.currentTarget.scrollTop;
    // The floating expand overlay is positioned in content coordinates of a
    // moving viewport — hide it as soon as the grid scrolls.
    hideExpandOverlay();
    // Synchronous (two-grid parity with ResultsGrid's onScroll): the rAF
    // deferral this handler used to have added a full frame of latency to
    // every scroll. Its render-batching job is now done by the ROW_HEIGHT
    // quantization below — the virtualization window only changes when the
    // scroll position crosses a row boundary, so same-bucket updates bail out
    // (React skips the re-render when the returned state is unchanged).
    setScrollTop((prev) => (
      Math.floor(prev / ROW_HEIGHT) === Math.floor(nextScrollTop / ROW_HEIGHT) ? prev : nextScrollTop
    ));
  }, [hideExpandOverlay]);

  // Virtual row calculations
  const totalRows = allRows.length;
  // Fast-scroll deferral (two-grid parity with ResultsGrid): the EXPENSIVE
  // window (the one real rows render from) follows scrollTop through
  // useDeferredValue, so a burst of scroll events (trackpad flick, fast wheel
  // spin) never queues stale full-window renders — React abandons an
  // in-flight deferred render whenever a newer scroll position lands. While
  // the deferred window lags a fast scroll so far that it no longer overlaps
  // the live window, the cheap urgent render paints skeleton rows at the live
  // position instead, and the real rows mount ONCE — where the scroll
  // settles. Ordinary wheel steps keep overlapping windows, so they never see
  // skeletons.
  const deferredScrollTop = useDeferredValue(scrollTop);

  const computeWindow = useCallback((scrollPos: number) => {
    if (!totalRows) {
      return { startIndex: 0, endIndex: 0, topPaddingHeight: 0, bottomPaddingHeight: 0 };
    }

    const effectiveHeight = containerHeight || 600;

    const visibleStart = Math.floor(scrollPos / ROW_HEIGHT);
    const visibleEnd = Math.ceil((scrollPos + effectiveHeight) / ROW_HEIGHT);

    const start = Math.max(0, visibleStart - DEFAULT_OVERSCAN);
    const end = Math.min(totalRows, visibleEnd + DEFAULT_OVERSCAN);

    return {
      startIndex: start,
      endIndex: end,
      topPaddingHeight: start * ROW_HEIGHT,
      bottomPaddingHeight: Math.max(0, (totalRows - end) * ROW_HEIGHT),
    };
  }, [containerHeight, totalRows]);

  const { startIndex, endIndex, topPaddingHeight, bottomPaddingHeight } = useMemo(
    () => computeWindow(deferredScrollTop),
    [computeWindow, deferredScrollTop]
  );

  // Live (urgent) window — where the user actually is right now. Null while
  // the deferred window is caught up (the common, settled case).
  const urgentWindow = useMemo(
    () => (deferredScrollTop === scrollTop ? null : computeWindow(scrollTop)),
    [computeWindow, scrollTop, deferredScrollTop]
  );

  // Fast-scroll gap: the live window shares no rows with the lagging deferred
  // window — paint skeletons at the live position instead of offscreen rows.
  const fastScrollGapWindow =
    urgentWindow && (urgentWindow.startIndex >= endIndex || urgentWindow.endIndex <= startIndex)
      ? urgentWindow
      : null;

  // Trimmed skeleton window for the gap branch: while skeletons are showing,
  // the scroll is still in flight — overscan rows would only make the cheap
  // urgent commit less cheap. Trims up to the overscan from each side and
  // moves the trimmed height into the paddings, so scroll geometry is exact.
  const gapSkeletonWindow = useMemo(() => {
    if (!fastScrollGapWindow) return null;
    return trimGapSkeletonWindow(fastScrollGapWindow, containerHeight || 600, ROW_HEIGHT, DEFAULT_OVERSCAN);
  }, [fastScrollGapWindow, containerHeight]);

  const visibleRows = useMemo(
    () => allRows.slice(startIndex, endIndex),
    [endIndex, allRows, startIndex]
  );

  // A visible key that isn't in the cached pairs proves the lookup cache is
  // stale — refresh it once in the background (throttled per column) so the
  // label fills in. Mirrored in `common/components/ResultsGrid.tsx`.
  useLookupLabelSelfHeal({
    connectionId,
    database,
    engine,
    metaInfo,
    lookupLabelInfos: cachedLookupLabelInfos,
    columns: lookupLabelColumns,
    rows: visibleRows,
    metadataEditors: host?.metadataEditors,
  });

  // The other half of the label pipeline, for targets flagged
  // `mode: 'on-demand'`: the same visible window, resolved by small batched
  // queries and never written to MetaInfo. `useLookupLabelMaps` emits nothing
  // for those columns and this emits nothing for cached ones, so the two maps
  // are disjoint by construction — the merge below can't shadow either.
  const onDemandLookupLabelInfos = useOnDemandLookupLabels({
    connectionId,
    database,
    metaInfo,
    fkLookupTargets,
    hostLookupDeclarations,
    columns: lookupLabelColumns,
    rows: visibleRows,
    relationshipPort: host?.relationships,
  });

  // ONE map for the cells: the suffix, its tooltip, the copy-raw invariant and
  // the edit-mode rendering all ride it without knowing which half a label
  // came from.
  const lookupLabelInfos = useMemo(() => {
    if (onDemandLookupLabelInfos.size === 0) return cachedLookupLabelInfos;
    return new Map([...cachedLookupLabelInfos, ...onDemandLookupLabelInfos]);
  }, [cachedLookupLabelInfos, onDemandLookupLabelInfos]);

  // Reset manual column sizing when visible columns change
  useEffect(() => {
    setColumnSizing({});
    setIsFixedLayout(false);
  }, [visibleColumns]);

  // Capture current column widths from DOM and switch to fixed layout.
  // Cells are matched by `data-column-id`, not by position: this header row also
  // carries the leading row-details gutter, the edit-mode action cells and the
  // inert PP-05 filler, so an index-based map handed every column its
  // neighbour's width (measured: pinning shifted `b` from 52.65px to 50px, and
  // with columns of differing widths the shift is the full column width).
  const lockColumnWidths = useCallback(() => {
    if (isFixedLayout) return;

    const table = tableRef.current;
    if (!table) return;

    const headerCells = table.querySelectorAll<HTMLElement>('thead th[data-column-id]');
    const newSizing: ColumnSizingState = {};

    headerCells.forEach((cell) => {
      const columnId = cell.dataset.columnId;
      if (columnId) {
        newSizing[columnId] = cell.getBoundingClientRect().width;
      }
    });

    setColumnSizing(newSizing);
    setIsFixedLayout(true);
  }, [isFixedLayout]);

  // ── Memoized-row support (two-grid parity with ResultsGrid) ───────────
  // ONE ctx object for <TableExplorerGridRow>. Every function member is one of
  // this component's useCallbacks, so the memo below invalidates exactly when
  // something row-visible changes (pending edits, selection, sizing, pinning,
  // metadata) — and never on a pure scroll.
  const buildEditSmartContext = useCallback((columnName: string, rowData: Record<string, unknown>) => {
    const columnDef = columnDefById.get(columnName);
    if (!columnDef) return undefined;
    return (engine && connectionId && database && sourceSchemaForTable && sourceTableForTable)
      ? {
          connectionId,
          database,
          engine,
          schema: sourceSchemaForTable,
          table: sourceTableForTable,
          // Implicit lookup enums edit exactly like explicit ones: the enum
          // editor with the FK row-picker escape (`resolveCellEditVariant`).
          enumDeclared: enumDeclaredLikeColumnIds.has(columnDef.id),
          enumNative: nativeEnumColumnIds.has(columnDef.id),
          realFkTarget: columnDef.fkInfo && !columnDef.fkInfo.isLoose
            ? {
                schema: columnDef.fkInfo.referencedSchema ?? '',
                table: columnDef.fkInfo.referencedTable,
                column: columnDef.fkInfo.referencedColumn,
              }
            : undefined,
          looseRelCandidates: looseRelsByColumn.get(columnName) ?? [],
          sourceRow: rowData,
          // Host port (Azure Table): smart editors read through it, never SQL (S4 review C, M7).
          ...(host?.relationships ? { relationshipPort: host.relationships } : {}),
        }
      : undefined;
  }, [columnDefById, engine, connectionId, database, sourceSchemaForTable, sourceTableForTable, enumDeclaredLikeColumnIds, nativeEnumColumnIds, looseRelsByColumn, host?.relationships]);

  // ── Column widths via <colgroup>, pinned offsets via CSS variables ─────
  // Two-grid parity with ResultsGrid: rows must not depend on `columnSizing`
  // (per-td widths made every resize-drag mousemove re-render all visible
  // rows). Explicit widths live on <col> elements; pinned sticky offsets are
  // CSS custom properties on the <table>, referenced by var() from cells.
  const pinnedStyleByColumnId = useMemo(() => {
    const map = new Map<string, { side: 'left' | 'right'; varName: string; isEdge: boolean }>();
    (columnPinning.left ?? []).forEach((id, i, arr) => {
      map.set(id, { side: 'left', varName: `--grid-pin-l-${i}`, isEdge: i === arr.length - 1 });
    });
    (columnPinning.right ?? []).forEach((id, i) => {
      map.set(id, { side: 'right', varName: `--grid-pin-r-${i}`, isEdge: i === 0 });
    });
    return map;
  }, [columnPinning]);

  const pinnedOffsetVars = useMemo(() => {
    const vars: Record<string, string> = {};
    (columnPinning.left ?? []).forEach((id, i) => {
      vars[`--grid-pin-l-${i}`] = `${getPinnedLeftOffset(id, columnSizing, MIN_COLUMN_WIDTH)}px`;
    });
    (columnPinning.right ?? []).forEach((id, i) => {
      vars[`--grid-pin-r-${i}`] = `${getPinnedRightOffset(id, columnSizing, MIN_COLUMN_WIDTH)}px`;
    });
    return vars;
  }, [columnPinning, columnSizing, getPinnedLeftOffset, getPinnedRightOffset]);

  const rowCtx = useMemo<TableExplorerRowCtx>(() => ({
    columnDefById,
    expandIconClassById,
    numericColumnIds,
    groupedNumericColumnIds,
    numericGrouping,
    minColumnWidth: MIN_COLUMN_WIDTH,
    maxAutoColumnWidth: MAX_AUTO_COLUMN_WIDTH,
    rowHeight: ROW_HEIGHT,
    isEditMode,
    showRowDetailsExpander,
    absentValueGlyph: hostAbsentValueGlyph,
    cellValueCodec: host?.cellValueCodec,
    relationshipPort: host?.relationships,
    insertedRowNote: host?.insertedRowNote,
    displayText: host?.displayText,
    isHostCellEditable,
    connectionId,
    database,
    engine,
    sourceSchemaForTable,
    sourceTableForTable,
    lobColumns,
    onFetchFullCellValue,
    nativeSpatialColumnIds,
    declaredSpatialColumnIds,
    declaredSpatialColumns,
    forcedJsonColumnIds,
    declaredJsonColumnIds,
    jsonColumnIdSet,
    looseRelationships: metaInfo?.looseRelationships,
    looseRelsByColumn,
    lookupLabelInfos,
    pinnedStyleByColumnId,
    isCellSelected: isCellSelectedStable,
    onCellMouseDown: onCellMouseDownStable,
    onCellMouseEnter: onCellMouseEnterStable,
    getRowPendingState,
    getPrimaryKeyFromRow,
    onRowSelect,
    onToggleRowDetails,
    handleStartQuickEdit,
    handleQuickEditConfirm,
    handleQuickEditCancel,
    handleStartFullEdit,
    handleFullEditConfirm,
    handleFullEditCancel,
    handleStartExpandedEdit,
    handleDeleteRow,
    handleUndoDeleteRow,
    handleRemoveInsertRow,
    setExpandedEditState,
    buildEditSmartContext,
  }), [
    columnDefById, expandIconClassById, numericColumnIds, groupedNumericColumnIds, numericGrouping,
    isEditMode, showRowDetailsExpander, hostAbsentValueGlyph, isHostCellEditable,
    host?.cellValueCodec, host?.relationships, host?.insertedRowNote, host?.displayText,
    connectionId, database, engine,
    sourceSchemaForTable, sourceTableForTable, lobColumns, onFetchFullCellValue,
    nativeSpatialColumnIds, declaredSpatialColumnIds, declaredSpatialColumns,
    forcedJsonColumnIds, declaredJsonColumnIds, jsonColumnIdSet,
    metaInfo?.looseRelationships, looseRelsByColumn, lookupLabelInfos,
    pinnedStyleByColumnId,
    isCellSelectedStable, onCellMouseDownStable, onCellMouseEnterStable,
    getRowPendingState, getPrimaryKeyFromRow, onRowSelect, onToggleRowDetails,
    handleStartQuickEdit, handleQuickEditConfirm, handleQuickEditCancel,
    handleStartFullEdit, handleFullEditConfirm, handleFullEditCancel, handleStartExpandedEdit,
    handleDeleteRow, handleUndoDeleteRow, handleRemoveInsertRow, buildEditSmartContext,
  ]);

  // Which cell the delegated context menu targets, resolved to its row + value.
  const menuRowData = menuCell ? (allRows[menuCell.rowIndex] ?? null) : null;
  const menuColumnDef = menuCell ? columnDefById.get(menuCell.columnId) : undefined;
  const menuIsInsertedRow = !!menuRowData && '_tempId' in menuRowData;
  const menuRowDeleted = !!menuRowData && !menuIsInsertedRow && isRowDeleted(menuRowData);
  const menuIsPrimaryKeyColumn = menuColumnDef?.isPrimaryKey || false;
  const menuCellValue = menuCell && menuRowData ? menuRowData[menuColumnDef?.key ?? menuCell.columnId] : undefined;

  // Delegated right-click handler: resolve the target cell from the DOM (every
  // data cell carries `data-row-index`/`data-column-id`) before Radix's own
  // bubble-phase trigger handler opens the menu. Non-cell targets (the PP-05
  // filler, virtualization padding, the expander gutter, the edit-mode actions
  // column, skeleton rows) suppress the menu.
  const handleGridBodyContextMenuCapture = useCallback((event: React.MouseEvent) => {
    const cellEl = (event.target as HTMLElement).closest?.('td[data-row-index]') as HTMLElement | null;
    if (!cellEl || cellEl.dataset.columnId === undefined) {
      event.stopPropagation();
      return;
    }
    setMenuCell({ rowIndex: Number(cellEl.dataset.rowIndex), columnId: cellEl.dataset.columnId });
  }, []);

  const handleCellMenuOpenChange = useCallback((open: boolean) => {
    if (!open) setMenuCell(null);
  }, []);

  // ── Delegated expand affordance ─────────────────────────────────────────
  // ONE floating expand button for the whole grid instead of a button + SVG
  // icon mounted inside every expandable cell. Cells advertise expandability
  // via `td[data-expand="text"|"json"]` (set in TableExplorerGridRow); the
  // mouseover delegation below positions this overlay over the hovered
  // cell's reserved `pl-5` gutter, so the affordance looks and clicks exactly
  // like the inline button it replaces. Show/hide/position are direct DOM
  // mutations on purpose — a React state update per hovered cell would
  // re-render the grid on every pointer move. On unsized-text tables (PG
  // `text`, SQLite `TEXT`, LOBs) the per-cell buttons measured ~2x slower
  // window mounts, which is what this delegation removes.
  const handleGridBodyMouseOver = useCallback((event: React.MouseEvent) => {
    const overlay = expandOverlayRef.current;
    const container = containerRef.current;
    if (!overlay || !container) return;
    const cellEl = (event.target as HTMLElement).closest?.('td[data-row-index]') as HTMLElement | null;
    const kind = cellEl?.dataset.expand;
    if (!cellEl || (kind !== 'text' && kind !== 'json')) {
      hideExpandOverlay();
      return;
    }
    const cellRect = cellEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    // Land inside the cell's reserved gutter: the td has `px-3` (12px), and
    // the inline affordance this replaces sat at `left-0` of the padded
    // wrapper; the button is 16px (`h-4 w-4`) tall, centered in the row.
    overlay.style.left = `${cellRect.left - containerRect.left + container.scrollLeft + 12 * zoomLevel}px`;
    overlay.style.top = `${cellRect.top - containerRect.top + container.scrollTop + (cellRect.height - 16) / 2}px`;
    overlay.style.display = 'inline-flex';
    const textIcon = overlay.querySelector<HTMLElement>('[data-expand-icon="text"]');
    const jsonIcon = overlay.querySelector<HTMLElement>('[data-expand-icon="json"]');
    if (textIcon) textIcon.style.display = kind === 'text' ? '' : 'none';
    if (jsonIcon) jsonIcon.style.display = kind === 'json' ? '' : 'none';
    expandOverlayTargetRef.current = { rowIndex: Number(cellEl.dataset.rowIndex), columnId: cellEl.dataset.columnId ?? '' };
  }, [hideExpandOverlay, zoomLevel]);

  const handleGridBodyMouseLeave = useCallback((event: React.MouseEvent) => {
    // Moving from the cell ONTO the overlay leaves the tbody — that must not
    // hide the button the pointer is about to click.
    //
    // `relatedTarget` is an EventTarget, NOT necessarily a Node: leaving
    // toward the window edge / devtools can hand back `window` itself, and
    // `Node.contains()` THROWS a TypeError on non-Node arguments (field
    // crash report: "parameter 1 is not of type 'Node'"). Guard with
    // instanceof before the containment check.
    const related = event.relatedTarget;
    if (related instanceof Node && expandOverlayRef.current?.contains(related)) return;
    hideExpandOverlay();
  }, [hideExpandOverlay]);

  const handleExpandOverlayClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    const target = expandOverlayTargetRef.current;
    if (!target) return;
    const rowData = allRows[target.rowIndex];
    const columnDef = columnDefById.get(target.columnId);
    if (!rowData || !columnDef) return;
    // Same value + mode resolution the inline per-cell buttons used —
    // pending-aware value, content mode detected against the column shape
    // (plain objects resolve to 'json', matching the old hardcoded branch).
    const isInserted = '_tempId' in rowData;
    const pending = !isInserted ? getCellPendingValue(rowData, target.columnId) : { hasPending: false as const };
    const valueToShow = pending.hasPending ? pending.value : rowData[columnDef.key];
    // Same shape the inline buttons consulted: only `dataType` is relevant to
    // detectContentMode here (the old per-cell columnForDetection carried no
    // jsonClassification either).
    track('feature:te_expand_view_open', { entry: 'overlay' });
    const mode = detectContentMode(valueToShow, { dataType: columnDef.dataType });
    void handleStartExpandedEdit(target.rowIndex, target.columnId, valueToShow, mode, rowData);
  }, [allRows, columnDefById, getCellPendingValue, handleStartExpandedEdit, track]);

  // A data change can move rows under a shown overlay — hide rather than
  // track. Same for a quick/full edit starting in (or near) the targeted
  // cell: the editor replaces the cell content the overlay was anchored to.
  useEffect(() => {
    hideExpandOverlay();
  }, [allRows, quickEditCell, editingCell, hideExpandOverlay]);

  // Keyboard route to the expanded view (the delegated overlay is
  // pointer-only): with exactly ONE cell selected, Enter opens the same
  // dialog the overlay's click would. The scroll container carries
  // tabIndex={-1} (click-focusable) so clicking anywhere in the grid focuses
  // the container and the shortcut is live. (Query Editor parity: its expand
  // affordances are inline, per-cell focusable buttons, so it already has a
  // keyboard path.)
  const handleGridKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;
    // Only a "raw" Enter on the grid itself may open the expanded view.
    // Enter presses that some interactive descendant already consumed BUBBLE
    // here — through React portals too — so without these guards, confirming
    // a quick edit (EditableCell preventDefaults, doesn't stopPropagation)
    // reopened the PRE-edit value in the dialog, and keyboard-activating any
    // delegated-context-menu item (Radix preventDefaults Enter on click())
    // popped the dialog on top of the action it triggered.
    if (event.defaultPrevented) return;
    const target = event.target as HTMLElement;
    if (target.closest('button, input, textarea, select, [contenteditable="true"], [role="menuitem"], [role="dialog"]')) return;
    const selected = cellSelectionRef.current.selectedCells;
    if (selected.size !== 1) return;
    const { rowIndex, columnId } = parseCellKey(selected.values().next().value as string);
    const rowData = allRows[rowIndex];
    const columnDef = columnDefById.get(columnId);
    if (!rowData || !columnDef) return;
    const pending = getRowPendingState(rowData);
    const valueToShow = pending?.updates.has(columnId) ? pending.updates.get(columnId) : rowData[columnDef.key];
    const expandable = (lobColumns?.includes(columnId) ?? false)
      || shouldShowExpandIconForClass(expandIconClassById.get(columnId) ?? 'content-based', valueToShow);
    if (!expandable) return;
    event.preventDefault();
    track('feature:te_expand_view_open', { entry: 'keyboard' });
    const mode = detectContentMode(valueToShow, { dataType: columnDef.dataType });
    void handleStartExpandedEdit(rowIndex, columnId, valueToShow, mode, rowData);
  }, [allRows, columnDefById, getRowPendingState, lobColumns, expandIconClassById, handleStartExpandedEdit, track]);

  // Host seam (S1 Task 1.17): SQL callers keep the loaded-window index id.
  const resolveRowId = useMemo(
    () => buildTableExplorerRowId(host, primaryKeyColumns),
    [host, primaryKeyColumns],
  );

  const table = useReactTable({
    data: visibleRows,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => resolveRowId(row, index, startIndex),
    columnResizeMode,
    enableColumnResizing: true,
    state: {
      columnSizing,
      columnPinning,
    },
    onColumnSizingChange: setColumnSizing,
    onColumnPinningChange,
  });

  // ── Memoized header ────────────────────────────────────────────────────
  // The header subtree (on wide tables: per-column dnd-kit sortable bindings
  // + a Radix context menu each) is expensive and depends on nothing
  // scroll-related. Memoizing the ELEMENT lets React bail out of the whole
  // <thead> on scroll-triggered renders — measured as the dominant remaining
  // per-scroll cost after row memoization.
  const tableHeaderGroups = table.getHeaderGroups();
  // Live-resize flag: the drag handle's active highlight must clear on release,
  // which changes no other dep of the header memo.
  const isResizingHeaderColumn = table.getState().columnSizingInfo.isResizingColumn;
  const headerContent = useMemo(() => (
          <thead>
            {tableHeaderGroups.map((headerGroup) => (
              <tr key={headerGroup.id}>
                {showRowDetailsExpander && (
                  <th
                    className="px-2 py-1.5 text-left font-medium text-xs
                               bg-muted border-b border-r border-border
                               text-muted-foreground whitespace-nowrap"
                    // `minWidth` as well as `width`: under `table-layout: auto`
                    // the greedy PP-05 filler would otherwise squeeze this
                    // fixed-purpose gutter to its 32px min-content, then let it
                    // spring back to 34px when the layout flips to fixed.
                    style={{ width: 34, minWidth: 34, position: 'sticky', top: 0, zIndex: 25 }}
                  />
                )}
                <DndContext
                  sensors={dndSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleHeaderDragEnd}
                  // Portal dnd-kit's hidden screen-reader nodes to <body>. By
                  // default they render inline, and since this DndContext lives
                  // inside the header <tr>, the browser wraps those stray <div>s
                  // in anonymous table cells — adding phantom columns that the
                  // body rows lack and pushing the edit-mode delete-column header
                  // out of alignment with the per-row delete buttons.
                  accessibility={{ container: document.body }}
                >
                  <SortableContext
                    items={headerGroup.headers.map((h) => h.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                {headerGroup.headers.map((header) => {
                  const hasExplicitWidth = columnSizing[header.id] !== undefined;
                  const columnName = header.id;
                  const normalizedColumn = normalizeColumnName(columnName);
                  const hasSort = sortedColumnSet.has(normalizedColumn);
                  const isPinned = header.column.getIsPinned();
                  const colMeta = normalizedColumns.find((c) => c.name === columnName);
                  const hasLooseFk = looseRelsByColumn.has(columnName);
                  const looseFkEligible =
                    hostRelationshipNavigation &&
                    !hasLooseFk &&
                    !colMeta?.isForeignKey &&
                    isLooseFkCandidateType(colMeta?.dataType);
                  const headerPinnedStyle = isPinned === 'left' ? {
                    position: 'sticky' as const,
                    left: getPinnedLeftOffset(header.id, columnSizing, MIN_COLUMN_WIDTH),
                    top: 0,
                    zIndex: 30,
                    ...(isLastPinnedLeft(header.id) ? pinnedColumnBorderStyle('left') : {}),
                  } : isPinned === 'right' ? {
                    position: 'sticky' as const,
                    right: getPinnedRightOffset(header.id, columnSizing, MIN_COLUMN_WIDTH),
                    top: 0,
                    zIndex: 30,
                    ...(isFirstPinnedRight(header.id) ? pinnedColumnBorderStyle('right') : {}),
                  } : {
                    position: 'sticky' as const,
                    top: 0,
                    zIndex: 25,
                  };

                  return (
                    <SortableHeaderBindings
                      key={header.id}
                      id={header.id}
                      disabled={!onColumnReorder || !!isPinned}
                    >
                      {(sortable) => (
                        <ContextMenu>
                          <ContextMenuTrigger asChild>
                        <th
                          ref={sortable.setNodeRef}
                          {...sortable.attributes}
                          {...sortable.listeners}
                          // A host grid offers no column reorder, so dnd-kit would mark every header
                          // `aria-disabled="true"` although the header still sorts, filters and opens its
                          // menu (Playwright then refuses to click it). SQL headers keep dnd-kit's value,
                          // exactly as at base (S4 review F, F15).
                          {...(host ? { 'aria-disabled': undefined } : {})}
                          // Lets `lockColumnWidths` map a measured <th> back to
                          // its column by id instead of by position — this row
                          // also carries the leading row-details gutter, the
                          // edit-mode action cells and the filler, and a
                          // positional map silently assigns each column its
                          // neighbour's width.
                          data-column-id={header.id}
                          className={`relative px-3 py-1.5 ${numericColumnIds.has(header.id) ? 'text-right' : 'text-left'} font-medium text-xs
                                     bg-muted border-b border-r border-border last:border-r-0
                                     text-muted-foreground whitespace-nowrap select-none overflow-hidden text-ellipsis ${
                                       onColumnReorder && !isPinned
                                         ? 'cursor-grab active:cursor-grabbing'
                                         : 'cursor-pointer'
                                     } hover:text-foreground`}
                          style={{
                            ...(hasExplicitWidth
                              ? { width: header.getSize() }
                              : { minWidth: MIN_COLUMN_WIDTH, maxWidth: MAX_AUTO_COLUMN_WIDTH }),
                            ...headerPinnedStyle,
                            ...sortable.style,
                          }}
                          onClick={() => {
                            if (sortable.isDragging) return;
                            if (!onAddSort || hasSort) return;
                            onAddSort(columnName);
                          }}
                          title={onColumnReorder ? 'Drag to reorder, click to sort' : 'Click to sort'}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {/* Resize handle.
                              `flushSync` matters: TanStack's resize handler reads
                              `header.getSize()` at event time, so without a
                              synchronous commit it sees the pre-lock state and
                              starts the drag from the 150px default instead of the
                              column's rendered width — the column jumped to 150px
                              the moment you grabbed its border. */}
                          <div
                            onPointerDown={(e) => e.stopPropagation()}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              flushSync(() => lockColumnWidths());
                              header.getResizeHandler()(e);
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              flushSync(() => lockColumnWidths());
                              header.getResizeHandler()(e);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            // G-3 — hover ordering, identical to the twin in
                            // `GridHeaderRow.tsx` (see the full rationale there):
                            // `hover:bg-primary/50` at (0,0,2,0) outranked the
                            // plain `bg-primary` resizing class at (0,0,1,0), so
                            // the handle got WEAKER when hovered mid-resize. One
                            // `hover:bg-*` utility is emitted, chosen in JS.
                            className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none
                                        active:bg-primary
                                        ${header.column.getIsResizing() ? 'bg-primary hover:bg-primary' : 'hover:bg-primary/50'}`}
                          />
                        </th>
                      </ContextMenuTrigger>
                      {/* `onCloseAutoFocus` is prevented so Radix doesn't restore
                          focus to the <th> trigger after an action runs — the
                          actions here (Add to Filter / Sort, Pin, Profile) all
                          hand control to a different UI surface, and on
                          "Add to Filter" the trigger would otherwise steal focus
                          from the draft chip's value input that just mounted. */}
                      <ContextMenuContent onCloseAutoFocus={(e) => e.preventDefault()}>
                        <ContextMenuItem
                          onClick={() => onAddFilter?.(columnName)}
                          disabled={!onAddFilter}
                          className="text-xs"
                        >
                          <Filter className="h-3.5 w-3.5 mr-2" />
                          Add to Filter
                        </ContextMenuItem>
                        <ContextMenuItem
                          onClick={() => onAddSort?.(columnName)}
                          disabled={hasSort || !onAddSort}
                          className="text-xs"
                        >
                          <ArrowUpDown className="h-3.5 w-3.5 mr-2" />
                          {hasSort ? 'Already sorted' : 'Add to Sort'}
                        </ContextMenuItem>
                        {isPinned ? (
                          <ContextMenuItem
                            onClick={() => unpinColumn(columnName)}
                            className="text-xs"
                          >
                            <Pin className="h-3.5 w-3.5 mr-2" />
                            Unpin column
                          </ContextMenuItem>
                        ) : (
                          <>
                            <ContextMenuItem
                              onClick={() => { lockColumnWidths(); pinColumn(columnName, 'left'); }}
                              className="text-xs"
                            >
                              <Pin className="h-3.5 w-3.5 mr-2" />
                              Pin to left
                            </ContextMenuItem>
                            <ContextMenuItem
                              onClick={() => { lockColumnWidths(); pinColumn(columnName, 'right'); }}
                              className="text-xs"
                            >
                              <Pin className="h-3.5 w-3.5 mr-2" />
                              Pin to right
                            </ContextMenuItem>
                          </>
                        )}
                        <ContextMenuItem
                          onClick={() => {
                            const col = normalizedColumns.find((c) => c.id === columnName);
                            if (!col) return;
                            const values = table.getRowModel().rows.map((r) => spatialCopyTransform((r.original as Record<string, unknown>)[col.key], col.id));
                            const text = columnValuesToCsvList(values);
                            void writeRichToClipboard(text, '');
                            track('feature:te_copy_column_values', { count: values.length });
                          }}
                          className="text-xs"
                          data-testid="table-explorer-header-copy-column-values"
                        >
                          <Copy className="h-3.5 w-3.5 mr-2" />
                          Copy values
                        </ContextMenuItem>
                        {onProfileColumn && (
                          <ContextMenuItem
                            onClick={() => onProfileColumn(columnName)}
                            className="text-xs"
                            data-testid="table-explorer-header-profile-column"
                          >
                            <BarChart3 className="h-3.5 w-3.5 mr-2" />
                            Profile column
                          </ContextMenuItem>
                        )}
                        {onFindUsages && (
                          <ContextMenuItem
                            onClick={() => onFindUsages(columnName)}
                            className="text-xs"
                            data-testid="table-explorer-header-find-usages"
                          >
                            <FileSearch className="h-3.5 w-3.5 mr-2" />
                            {FIND_USAGES_MENU_LABEL}
                          </ContextMenuItem>
                        )}
                        {declaredJsonColumnIds.has(header.id) && (
                          <ContextMenuItem
                            onClick={() => handleShowJsonDetails(columnName, 'header-menu')}
                            className="text-xs"
                            data-testid="table-explorer-header-json-details"
                          >
                            <FileJson className="h-3.5 w-3.5 mr-2" />
                            JSON details
                          </ContextMenuItem>
                        )}
                        {declarableJsonColumnIds.has(header.id) && !declaredJsonColumnIds.has(header.id) && (
                          <ContextMenuItem
                            onClick={() => handleDeclareAsJson(columnName, 'header-menu')}
                            className="text-xs"
                            data-testid="table-explorer-header-declare-json"
                          >
                            <Code2 className="h-3.5 w-3.5 mr-2" />
                            Declare as JSON
                          </ContextMenuItem>
                        )}
                        {/* Declared ∪ implicit — "Enum details" for both, and
                            the same set suppresses "Declare as enum" below. */}
                        {enumDeclaredLikeColumnIds.has(header.id) && (
                          <ContextMenuItem
                            onClick={() => handleEnumGlyphClick(header.id, 'declared')}
                            className="text-xs"
                            data-testid="table-explorer-header-enum-details"
                          >
                            <ChevronDown className="h-3.5 w-3.5 mr-2" />
                            Enum details
                          </ContextMenuItem>
                        )}
                        {nativeEnumColumnIds.has(header.id) && !enumDeclaredLikeColumnIds.has(header.id) && (
                          <ContextMenuItem
                            onClick={() => handleEnumGlyphClick(header.id, 'native')}
                            className="text-xs"
                            data-testid="table-explorer-header-configure-enum"
                          >
                            <ChevronDown className="h-3.5 w-3.5 mr-2" />
                            Configure enum column
                          </ContextMenuItem>
                        )}
                        {declarableEnumColumnIds.has(header.id) && !enumDeclaredLikeColumnIds.has(header.id) && !nativeEnumColumnIds.has(header.id) && (
                          <ContextMenuItem
                            onClick={() => handleEnumGlyphClick(header.id, 'eligible')}
                            className="text-xs"
                            data-testid="table-explorer-header-declare-enum"
                          >
                            <ChevronDown className="h-3.5 w-3.5 mr-2" />
                            Declare as enum
                          </ContextMenuItem>
                        )}
                        {declaredSpatialColumnIds.has(header.id) && (
                          <ContextMenuItem
                            onClick={() => handleSpatialColumnAction(header.id, 'declared')}
                            className="text-xs"
                            data-testid="table-explorer-header-spatial-details"
                          >
                            <MapPin className="h-3.5 w-3.5 mr-2" />
                            Coordinate details
                          </ContextMenuItem>
                        )}
                        {declarableSpatialColumnIds.has(header.id) && !declaredSpatialColumnIds.has(header.id) && (
                          <ContextMenuItem
                            onClick={() => handleSpatialColumnAction(header.id, 'declarable')}
                            className="text-xs"
                            data-testid="table-explorer-header-declare-spatial"
                          >
                            <MapPin className="h-3.5 w-3.5 mr-2" />
                            Declare as coordinates…
                          </ContextMenuItem>
                        )}
                        {hasLooseFk && (
                          <ContextMenuItem
                            onClick={() => handleEditLooseFkFromMenu(columnName, 'header-menu')}
                            className="text-xs"
                            data-testid="table-explorer-header-edit-loose-fk"
                          >
                            <Pencil className="h-3.5 w-3.5 mr-2" />
                            Edit loose relationship
                          </ContextMenuItem>
                        )}
                        {looseFkEligible && (
                          <ContextMenuItem
                            onClick={() => handleDeclareLooseFkFromMenu(columnName, 'header-menu')}
                            className="text-xs"
                            data-testid="table-explorer-header-declare-loose-fk"
                          >
                            <Link2 className="h-3.5 w-3.5 mr-2" />
                            Declare loose relationship
                          </ContextMenuItem>
                        )}
                      </ContextMenuContent>
                    </ContextMenu>
                      )}
                    </SortableHeaderBindings>
                  );
                })}
                  </SortableContext>
                </DndContext>
                {/* Actions column header in edit mode. The PP-05 filler <th> is
                    appended after it so the header band reaches the right edge. */}
                {isEditMode && (
                  <th
                    className="px-2 py-1.5 text-left font-medium text-xs
                               bg-muted border-b border-r border-border last:border-r-0
                               text-muted-foreground whitespace-nowrap"
                    style={{ width: 40 }}
                    data-testid="table-explorer-delete-header"
                  >
                    {/* Match the per-row delete button's 24px (`h-6 w-6`)
                        centered box so the header trash glyph column-aligns with
                        the per-row trash icons rather than sitting left of them. */}
                    <span className="inline-flex h-6 w-6 items-center justify-center">
                      <Trash2 className="h-3.5 w-3.5" />
                    </span>
                  </th>
                )}
                {/* PP-05 — inert filler carrying the header band to the right edge.
                    Deliberately NOT a TanStack column: it can't be sorted, filtered,
                    resized, reordered, pinned or selected, and `getVisibleCells()`
                    never yields it, so copy/export are structurally unaffected.

                    `width: 100%` is what makes the slack actually land here. Under
                    `table-layout: auto` the `max-width` on the data cells is inert,
                    so without a greedy filler the columns stretched to absorb the
                    pane (measured: 512/540px on a 2-column table, filler 5px). A
                    cell asking for the whole table width is served last, after
                    every other column has taken its content width. Measured inert
                    under `table-layout: fixed` and when the grid overflows. */}
                <th
                  aria-hidden="true"
                  data-grid-filler="true"
                  data-testid="grid-filler-header"
                  className="p-0 bg-muted border-b border-border select-none"
                  style={{ width: '100%' }}
                />
              </tr>
            ))}
          </thead>
  // isResizingHeaderColumn looks "unnecessary" to the lint (the body reads it
  // through the stable `table` instance), but it is a real invalidation
  // signal — the drag handle's highlight must clear on release.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [tableHeaderGroups, isResizingHeaderColumn, columnSizing, declarableEnumColumnIds, declarableJsonColumnIds, declarableSpatialColumnIds, enumDeclaredLikeColumnIds, declaredJsonColumnIds, declaredSpatialColumnIds, dndSensors, getPinnedLeftOffset, getPinnedRightOffset, handleDeclareAsJson, handleDeclareLooseFkFromMenu, handleEditLooseFkFromMenu, handleEnumGlyphClick, handleHeaderDragEnd, handleShowJsonDetails, handleSpatialColumnAction, hostRelationshipNavigation, isEditMode, isFirstPinnedRight, isLastPinnedLeft, lockColumnWidths, looseRelsByColumn, nativeEnumColumnIds, normalizedColumns, numericColumnIds, onAddFilter, onAddSort, onColumnReorder, onFindUsages, onProfileColumn, pinColumn, showRowDetailsExpander, sortedColumnSet, spatialCopyTransform, table, track, unpinColumn]);

  // U13 (user-reported): "if big dataset 'Add row' adds empty row at bottom
  // -> but should scroll it to view and focus on first editable cell." A
  // NET-NEW pending insert (length increases — a tab resumed with pending
  // inserts already on it is deliberately a no-op) is always the LAST row in
  // `allRows` (inserts are appended there, never anywhere else). Scroll it
  // into view the same way the `selectedRowIndex` effect above does — mutate
  // `container.scrollTop` directly and let the native scroll event settle
  // `startIndex`/`endIndex` — and set `editingCell` to its first EDITABLE
  // column.
  //
  // "First editable" reuses `isCellEditable`'s own rule (a few hundred lines
  // below) rather than re-deriving it: identity columns are never editable,
  // and `isInsertedRow` makes every non-identity column fair game on a brand
  // new row, PK included. Column order comes from the table instance's own
  // resolved `getVisibleLeafColumns()` — not a re-derivation from
  // `normalizedColumns` — so pinning/reordering can't desync "first" from
  // what actually renders first.
  //
  // No setTimeout: `editingCell` is independent of what is currently
  // virtualized, so setting it before the row scrolls into view is harmless
  // — whichever render first includes the row (gated by the virtualizer's
  // own `startIndex`/`endIndex` window, exactly like every other row) mounts
  // it already in "full editing" mode, and `EditableCell`'s existing
  // focus-on-mount effect (`useEffect` on `isEditing`) does the actual
  // `.focus()` at that point — the row's own mount IS the "wait until
  // rendered" mechanism, not a guessed delay.
  const previousInsertCountRef = useRef(insertedRows.length);
  useEffect(() => {
    const previousCount = previousInsertCountRef.current;
    previousInsertCountRef.current = insertedRows.length;
    if (insertedRows.length <= previousCount) return;

    const container = containerRef.current;
    if (!container) return;

    const newRowIndex = allRows.length - 1;
    const rowTop = newRowIndex * ROW_HEIGHT;
    const rowBottom = rowTop + ROW_HEIGHT;
    const viewTop = container.scrollTop;
    const viewBottom = viewTop + container.clientHeight;
    if (rowBottom > viewBottom) {
      container.scrollTop = rowBottom - container.clientHeight;
    } else if (rowTop < viewTop) {
      container.scrollTop = rowTop;
    }

    // The host's editability veto applies here too (S4 review C, N2): an
    // Azure Table key is never edited in the grid, so the editor opens on the
    // first property instead. No host = every SQL engine = no veto, unchanged.
    const newRow = allRows[newRowIndex];
    const firstEditableColumn = table.getVisibleLeafColumns().find((col) => {
      const def = columnDefById.get(col.id);
      return !!def && !def.isIdentity && (!newRow || isHostCellEditable(col.id, newRow));
    });
    if (firstEditableColumn) {
      setEditingCell({ rowIndex: newRowIndex, columnId: firstEditableColumn.id });
    }
  }, [insertedRows, allRows, table, columnDefById, isHostCellEditable]);

  const containerClassName = 'w-full h-full flex flex-col overflow-hidden bg-background';

  // Error state
  if (error) {
    return (
      <div
        className={containerClassName}
        data-testid="table-explorer-grid"
        data-render-count={renderCountRef.current}
      >
        <div
          className="flex-1 flex flex-col items-center justify-center gap-4"
          data-testid="table-explorer-error"
        >
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span className="text-sm font-medium">Failed to load data</span>
          </div>
          <p className="text-sm text-muted-foreground max-w-md text-center">{error}</p>
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Loading state — skeleton grid stand-in.
  // - Only fires when there are no rows yet to display. As soon as populating batches
  //   begin arriving via `populationProgress` (and the parent maps them into `rows`),
  //   the grid switches off the skeleton and renders the partial rows progressively.
  // - When columns are already known (refresh / page-size / filter change) we render the
  //   real header row and lay out skeleton cells with the same fixed per-column min-width
  //   the live grid uses, so the skeleton matches the prior layout instead of flashing
  //   a generic 6-column block (or a 30-narrow-column squish).
  // - When columns are not yet known (first open) we fall back to a 6-column generic skeleton.
  if (isLoading && rows.length === 0) {
    const skeletonColumnCount = columns.length > 0 ? columns.length : 6;
    const cellMinWidthPx = 150;
    const tableMinWidth = `${skeletonColumnCount * cellMinWidthPx}px`;
    return (
      <div
        className={containerClassName}
        data-testid="table-explorer-grid"
        data-render-count={renderCountRef.current}
      >
        <div className="flex-1 overflow-auto">
          <table
            className="border-collapse text-xs"
            style={{ minWidth: '100%', tableLayout: 'fixed', width: tableMinWidth }}
            aria-busy="true"
            aria-label="Loading table data"
          >
            <colgroup>
              {Array.from({ length: skeletonColumnCount }, (_, i) => (
                <col key={i} style={{ width: `${cellMinWidthPx}px` }} />
              ))}
            </colgroup>
            {columns.length > 0 && (
              <thead className="sticky top-0 z-10 bg-muted/50 border-b border-border">
                <tr className="h-8">
                  {columns.map((col) => (
                    <th
                      key={col.name}
                      className="px-3 text-left font-medium text-muted-foreground border-r border-border/30 last:border-r-0 truncate"
                      title={col.name}
                    >
                      {col.name}
                    </th>
                  ))}
                  {/* CH-25 — this loading-skeleton filler was missed by the
                      PP-05 filler sweep: same defect as the live header/body
                      fillers below (a native text selection over the
                      skeleton row would serialise a trailing tab), so it
                      gets the same `select-none` + greedy `width: 100%`. */}
                  <th
                    aria-hidden="true"
                    data-grid-filler="true"
                    className="p-0 select-none"
                    style={{ width: '100%' }}
                  />
                </tr>
              </thead>
            )}
            <tbody>
              <SkeletonRows startIndex={0} count={20} columnCount={skeletonColumnCount} />
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Empty state is rendered inline inside <tbody> below so the column header
  // row stays visible (and interactive: sort, pin, filter, profile) when a
  // filter/page combination yields zero rows.

  return (
    <div
      className={containerClassName}
      data-testid="table-explorer-grid"
      data-render-count={renderCountRef.current}
    >
      <div
        ref={containerRef}
        onScroll={onScroll}
        onKeyDown={handleGridKeyDown}
        // tabIndex -1 (NOT 0): clicking any cell focuses this container via the
        // browser's nearest-focusable-ancestor rule, which is what makes the
        // Enter-to-expand shortcut above live after a cell click. It must stay
        // out of the tab order AND out of focus-restoration flows — a 0 here
        // let Radix's context-menu close-autofocus land on the container and
        // steal focus from the "Add to filter" draft input (measured: the
        // draft's Enter-to-confirm stopped working).
        tabIndex={-1}
        // [overflow-anchor:none]: standard virtualized-scroller hardening.
        // The fast-scroll gap branch swaps skeleton rows for real rows in
        // place; Chromium scroll anchoring may respond to such swaps by
        // adjusting scrollTop, which re-triggers the window computation - a
        // feedback loop that can stall a flick's settle until React's ~5s
        // transition expiry force-flushes it (observed rarely under the perf
        // gate). Anchoring has no value here: content height is fixed at
        // rowCount x ROW_HEIGHT, so there is nothing legitimate to anchor.
        className="relative flex-1 min-h-0 overflow-auto [overflow-anchor:none] focus:outline-none"
      >
        {/* PP-05 — `minWidth: 100%` keeps the grid spanning the pane when the
            columns are narrower than it; the trailing filler cell (header row and
            each body row) absorbs the slack so real columns keep their widths. */}
        <table
          ref={tableRef}
          className={`border-collapse text-sm ${isFixedLayout ? 'table-fixed' : ''}`}
          style={{ minWidth: '100%', ...(isFixedLayout ? { width: table.getTotalSize() } : {}), ...(zoomLevel !== 1 ? { zoom: zoomLevel } : {}), ...pinnedOffsetVars }}
        >
          {/* Explicit column widths live HERE (fixed layout) rather than on
              every td — a resize drag then repaints via colgroup + the CSS pin
              variables above with zero row re-renders (parity with
              ResultsGrid's colgroup). */}
          <colgroup>
            {showRowDetailsExpander && <col style={{ width: 34 }} />}
            {/* Pinning-AWARE order (left, center, right) — getVisibleLeafColumns()
                is visibility-only and would misalign the cols against the pinned
                header/cell order, handing columns their neighbours' widths on
                the auto->fixed flip. */}
            {[
              ...table.getLeftVisibleLeafColumns(),
              ...table.getCenterVisibleLeafColumns(),
              ...table.getRightVisibleLeafColumns(),
            ].map((column) => (
              <col
                key={column.id}
                style={columnSizing[column.id] !== undefined ? { width: column.getSize() } : undefined}
              />
            ))}
            {isEditMode && <col style={{ width: 40 }} />}
            {/* PP-05 filler column */}
            <col />
          </colgroup>
          {/* Sticky header — applied per-<th> (not on <thead>) because Chromium has
              a quirk where a sticky <th> inside a sticky <thead> only honors its
              horizontal offset; the vertical `top` anchor gets absorbed by the
              parent's sticky containing block, so pinned headers scroll away
              with the rows. */}
          {/* Header subtree — memoized element; see `headerContent` above. */}
          {headerContent}
          {/* ONE delegated context menu for every data cell. The per-cell
              Radix menu was the dominant scroll-render cost on wide tables
              (74 columns x ~50 rows = ~3,700 menu roots re-created per scroll
              tick). The tbody is the trigger; the capture handler resolves the
              target cell from `data-row-index`/`data-column-id` before Radix
              opens. */}
          <ContextMenu onOpenChange={handleCellMenuOpenChange}>
            <ContextMenuTrigger asChild>
          <tbody
            className={cellSelection.isMultiCell ? 'select-none' : ''}
            onContextMenuCapture={handleGridBodyContextMenuCapture}
            onMouseOver={handleGridBodyMouseOver}
            onMouseLeave={handleGridBodyMouseLeave}
          >
            {/* Fast-scroll gap: the live viewport has scrolled clear of the
                (deferred) rendered window — paint skeletons at the live
                position for instant feedback; the real rows mount once,
                where the scroll settles. */}
            {gapSkeletonWindow ? (
              <>
                {gapSkeletonWindow.topPaddingHeight > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={(normalizedColumns.length || 1) + (isEditMode ? 1 : 0) + (showRowDetailsExpander ? 1 : 0) + 1}
                      className="p-0 border-0"
                      style={{ height: gapSkeletonWindow.topPaddingHeight }}
                    />
                  </tr>
                )}
                <SkeletonRows
                  startIndex={gapSkeletonWindow.startIndex}
                  count={gapSkeletonWindow.count}
                  columnCount={normalizedColumns.length}
                />
                {gapSkeletonWindow.bottomPaddingHeight > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={(normalizedColumns.length || 1) + (isEditMode ? 1 : 0) + (showRowDetailsExpander ? 1 : 0) + 1}
                      className="p-0 border-0"
                      style={{ height: gapSkeletonWindow.bottomPaddingHeight }}
                    />
                  </tr>
                )}
              </>
            ) : (
              <>
              {/* Top padding for virtualization */}
              {topPaddingHeight > 0 && (
                <tr aria-hidden="true">
                  <td
                    colSpan={(normalizedColumns.length || 1) + (isEditMode ? 1 : 0) + (showRowDetailsExpander ? 1 : 0) + 1}
                    className="p-0 border-0"
                    style={{ height: topPaddingHeight }}
                  />
                </tr>
              )}

              {/* Visible rows — memoized: on scroll, rows already on screen skip
                  re-rendering; only rows entering the overscan window mount (see
                  TableExplorerGridRow). */}
              {table.getRowModel().rows.map((row, rowIndex) => {
                const actualRowIndex = startIndex + rowIndex;
                return (
                  <TableExplorerGridRow
                    key={row.id}
                    row={row}
                    actualRowIndex={actualRowIndex}
                    isSelectedRow={selectedRowIndex === actualRowIndex}
                    isRowDetailsExpanded={expandedRowDetailsIndex === actualRowIndex}
                    quickEditColumnId={quickEditCell?.rowIndex === actualRowIndex ? quickEditCell.columnId : null}
                    fullEditColumnId={isEditMode && editingCell?.rowIndex === actualRowIndex ? editingCell.columnId : null}
                    menuColumnId={menuCell?.rowIndex === actualRowIndex ? menuCell.columnId : null}
                    selectionSignature={selectionSignatureByRow?.get(actualRowIndex) ?? ''}
                    ctx={rowCtx}
                  />
                );
              })}


              {/* Empty state — keeps the column header row visible (and its
                  context-menu / sort / pin actions usable) when a filter or page
                  combination returns zero rows. */}
              {allRows.length === 0 && (
                <tr>
                  <td
                    colSpan={(normalizedColumns.length || 1) + (isEditMode ? 1 : 0) + (showRowDetailsExpander ? 1 : 0) + 1}
                    className="p-0"
                  >
                    <div
                      className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground"
                      data-testid="table-explorer-grid-empty-state"
                    >
                      {filteredColumns.length > 0 ? (
                        <>
                          <FilterX className="h-8 w-8 opacity-60" />
                          <p className="text-sm">No rows match the current filters.</p>
                        </>
                      ) : (
                        <>
                          <Inbox className="h-8 w-8 opacity-60" />
                          <p className="text-sm">No data to display.</p>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}

              {/* Bottom padding for virtualization */}
              {bottomPaddingHeight > 0 && (
                <tr aria-hidden="true">
                  <td
                    colSpan={(normalizedColumns.length || 1) + (isEditMode ? 1 : 0) + (showRowDetailsExpander ? 1 : 0) + 1}
                    className="p-0 border-0"
                    style={{ height: bottomPaddingHeight }}
                  />
                </tr>
              )}
              </>
            )}
          </tbody>
            </ContextMenuTrigger>
            {/* onCloseAutoFocus preventDefault (same as the header menus):
                with the scroll container click-focusable (tabIndex -1 for the
                Enter-to-expand route), Radix's default close-restore would
                focus IT — stealing focus from surfaces a menu action just
                opened (measured: the "Add to filter" draft input lost focus
                and its Enter-to-confirm stopped working). */}
            <ContextMenuContent onCloseAutoFocus={(event) => event.preventDefault()}>
              {/* Slice 1 (FK Breadcrumb / EP2) — Open record details */}
              {onOpenRecordDetails && primaryKeyColumns.length > 0 && (
                <ContextMenuItem
                  onClick={() => menuRowData && onOpenRecordDetails({ row: menuRowData })}
                  className="text-xs"
                  data-testid="row-context-open-record-details"
                >
                  <Maximize2 className="h-3.5 w-3.5 mr-2" />
                  Open record details
                </ContextMenuItem>
              )}
              {/* Slice 3 (FK Breadcrumb / EP2) — sibling items */}
              {onOpenRowInNewExplorer && primaryKeyColumns.length > 0 && (
                <ContextMenuItem
                  onClick={() => menuRowData && onOpenRowInNewExplorer({ row: menuRowData })}
                  className="text-xs"
                  data-testid="row-context-open-in-new-explorer"
                >
                  <Maximize2 className="h-3.5 w-3.5 mr-2 opacity-60" />
                  Open in new explorer
                </ContextMenuItem>
              )}
              {onOpenRowInQueryEditor && primaryKeyColumns.length > 0 && (
                <ContextMenuItem
                  onClick={() => menuRowData && onOpenRowInQueryEditor({ row: menuRowData })}
                  className="text-xs"
                  data-testid="row-context-open-in-query-editor"
                >
                  <Maximize2 className="h-3.5 w-3.5 mr-2 opacity-60" />
                  Open in query editor
                </ContextMenuItem>
              )}
              {/* Copy value option */}
              <ContextMenuItem
                onClick={() => {
                  if (!menuCell) return;
                  if (cellSelection.selectedCells.size > 1) {
                    const colKeyMap = new Map(normalizedColumns.map((c) => [c.id, c.key]));
                    const colNames = new Map(normalizedColumns.map((c) => [c.id, c.name]));
                    const columnIds = normalizedColumns.map((c) => c.id);
                    const text = buildCopyText(cellSelection.selectedCells, rows, columnIds, colKeyMap, 0, spatialCopyTransform);
                    const html = buildCopyHtml(cellSelection.selectedCells, rows, columnIds, colNames, colKeyMap, 0, spatialCopyTransform);
                    void writeRichToClipboard(text, html);
                  } else {
                    const copyValue = spatialCopyTransform(menuCellValue, menuCell.columnId);
                    const displayValue = copyValue === null || copyValue === undefined
                      ? ''
                      : typeof copyValue === 'object'
                        ? JSON.stringify(copyValue)
                        : String(copyValue);
                    void writeClipboardText(displayValue);
                  }
                }}
                className="text-xs"
                data-testid="copy-value-menu-item"
              >
                <Copy className="h-3.5 w-3.5 mr-2" />
                Copy value
              </ContextMenuItem>
              {/* Quick Edit option */}
              {canQuickEdit && !menuIsInsertedRow && !menuRowDeleted && !menuIsPrimaryKeyColumn && (
                <ContextMenuItem
                  onClick={() => menuCell && menuRowData && handleStartQuickEdit(menuCell.rowIndex, menuCell.columnId, menuCellValue, menuRowData)}
                  className="text-xs"
                  data-testid="quick-edit-menu-item"
                >
                  <Pencil className="h-3.5 w-3.5 mr-2" />
                  Edit
                </ContextMenuItem>
              )}
              {onAddFilterWithValue && (
                <ContextMenuItem
                  onClick={() => menuCell && onAddFilterWithValue(menuCell.columnId, menuCellValue)}
                  className="text-xs"
                  data-testid="context-menu-add-to-filter"
                >
                  <Filter className="h-3.5 w-3.5 mr-2" />
                  Add to filter
                </ContextMenuItem>
              )}
              {onProfileColumn && (
                <ContextMenuItem
                  onClick={() => menuCell && onProfileColumn(menuCell.columnId)}
                  className="text-xs"
                  data-testid="table-explorer-cell-profile-column"
                >
                  <BarChart3 className="h-3.5 w-3.5 mr-2" />
                  Profile this column
                </ContextMenuItem>
              )}
              {!!menuCell && declaredJsonColumnIds.has(menuCell.columnId) && (
                <ContextMenuItem
                  onClick={() => handleShowJsonDetails(menuCell.columnId, 'cell-menu')}
                  className="text-xs"
                  data-testid="table-explorer-cell-json-details"
                >
                  <FileJson className="h-3.5 w-3.5 mr-2" />
                  JSON details
                </ContextMenuItem>
              )}
              {!!menuCell && declarableJsonColumnIds.has(menuCell.columnId) && !declaredJsonColumnIds.has(menuCell.columnId) && (
                <ContextMenuItem
                  onClick={() => handleDeclareAsJson(menuCell.columnId, 'cell-menu')}
                  className="text-xs"
                  data-testid="table-explorer-cell-declare-json"
                >
                  <Code2 className="h-3.5 w-3.5 mr-2" />
                  Declare as JSON
                </ContextMenuItem>
              )}
              {!!menuCell && looseRelsByColumn.has(menuCell.columnId) && (
                <ContextMenuItem
                  onClick={() => handleEditLooseFkFromMenu(menuCell.columnId, 'cell-menu')}
                  className="text-xs"
                  data-testid="table-explorer-cell-edit-loose-fk"
                >
                  <Pencil className="h-3.5 w-3.5 mr-2" />
                  Edit loose relationship
                </ContextMenuItem>
              )}
              {hostRelationshipNavigation && !!menuCell && !looseRelsByColumn.has(menuCell.columnId) && !menuColumnDef?.isForeignKey && isLooseFkCandidateType(menuColumnDef?.dataType) && (
                <ContextMenuItem
                  onClick={() => handleDeclareLooseFkFromMenu(menuCell.columnId, 'cell-menu')}
                  className="text-xs"
                  data-testid="table-explorer-cell-declare-loose-fk"
                >
                  <Link2 className="h-3.5 w-3.5 mr-2" />
                  Declare loose relationship
                </ContextMenuItem>
              )}
              {spatialCompareCandidate && (
                <ContextMenuItem
                  onClick={() => setCompareDialogOpen(true)}
                  className="text-xs"
                  data-testid="table-explorer-cell-compare-geometry"
                >
                  <GitCompareArrows className="h-3.5 w-3.5 mr-2" />
                  Compare geometries
                </ContextMenuItem>
              )}
              {/* PP-01 — shared "Number display" submenu (parity with `CellContextMenu`). */}
              {!!menuCell && numericColumnIds.has(menuCell.columnId) && (
                <NumericGroupingMenu className="text-xs" />
              )}
            </ContextMenuContent>
          </ContextMenu>
        </table>
        {/* The ONE delegated expand button (see the handler block above).
            Positioned by the tbody mouseover delegation into the hovered
            expandable cell's reserved gutter; hidden at rest, on scroll, and
            when the pointer leaves. zIndex 21: above pinned cells (20), below
            the sticky header (25). React re-renders leave the imperative
            display/left/top mutations alone because the style prop below never
            changes identity-relevant values between renders. */}
        <button
          ref={expandOverlayRef}
          type="button"
          data-testid="cell-expand-btn"
          className={CELL_EXPAND_OVERLAY_BUTTON_CLASS}
          style={{ display: 'none', left: 0, top: 0, zIndex: 21 }}
          title={isEditMode ? 'Edit in expanded view' : 'View in expanded view'}
          aria-label={isEditMode ? 'Edit in expanded view' : 'View in expanded view'}
          onClick={handleExpandOverlayClick}
          onMouseLeave={hideExpandOverlay}
        >
          <span data-expand-icon="text" className="inline-flex"><Maximize2 className="h-3 w-3" aria-hidden="true" /></span>
          <span data-expand-icon="json" className="inline-flex" style={{ display: 'none' }}><Code2 className="h-3 w-3" aria-hidden="true" /></span>
        </button>
      </div>

      <CellAggregateBar aggregates={cellAggregates} />

      {/* Expanded Value Dialog for JSON/multiline text. When the column is
          a declared (or declared-eligible) JSON column for the current
          (connection, database, schema, table), pass `declaredJsonContext`
          so the dialog can surface the "Undeclare as JSON" affordance —
          the dialog itself resolves the declaration id from MetaInfo. */}
      <ExpandedValueDialog
        open={expandedEditState !== null}
        onOpenChange={(open) => {
          if (!open) setExpandedEditState(null);
        }}
        initialValue={formatValueForExpandedEdit(expandedEditState?.value, expandedEditState?.mode ?? 'text')}
        initialMode={expandedEditState?.mode ?? 'text'}
        onConfirm={handleExpandedEditConfirm}
        title={expandedEditState?.readOnly ? `View ${expandedEditState?.columnId ?? 'Value'}` : `Edit ${expandedEditState?.columnId ?? 'Value'}`}
        columnDataType={
          expandedEditState?.columnId
            ? normalizedColumns.find((c) => c.id === expandedEditState.columnId)?.dataType
            : undefined
        }
        readOnly={expandedEditState?.readOnly ?? false}
        onRequestEdit={expandedEditState?.canEdit ? () => {
          // Switch from read-only to edit mode
          setExpandedEditState((prev) => prev ? { ...prev, readOnly: false } : null);
        } : undefined}
        declaredJsonContext={
          expandedEditState?.columnId &&
          connectionId &&
          database &&
          sourceTableForTable &&
          (forcedJsonColumnIds.has(expandedEditState.columnId) ||
            declaredJsonColumnIds.has(expandedEditState.columnId))
            ? {
                connectionId,
                database,
                schema: sourceSchemaForTable ?? '',
                table: sourceTableForTable,
                column: expandedEditState.columnId,
                engine,
              }
            : undefined
        }
        declarableJsonContext={(() => {
          if (!expandedEditState?.columnId) return undefined;
          if (!engine || !connectionId || !database || !sourceTableForTable) return undefined;
          const column = normalizedColumns.find((c) => c.id === expandedEditState.columnId);
          if (!column) return undefined;
          if (forcedJsonColumnIds.has(expandedEditState.columnId)) return undefined;
          if (!declarableJsonColumnIds.has(expandedEditState.columnId)) return undefined;
          return {
            connectionId,
            database,
            schema: sourceSchemaForTable ?? '',
            table: sourceTableForTable,
            column: column.name,
            dataType: column.dataType,
            engine,
          };
        })()}
        spatial={expandedEditState?.spatial}
      />

      {/* T30 — two-cell geometry compare */}
      {spatialCompareCandidate && (
        <SpatialCompareDialog
          open={compareDialogOpen}
          onOpenChange={setCompareDialogOpen}
          a={{ input: spatialCompareCandidate.a.input, fetchFullValue: spatialCompareCandidate.a.fetchFullValue }}
          b={{ input: spatialCompareCandidate.b.input, fetchFullValue: spatialCompareCandidate.b.fetchFullValue }}
          connectionId={connectionId}
          database={database}
        />
      )}
    </div>
  );
});
